import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Activity,
  AlertTriangle,
  BrainCircuit,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  Dumbbell,
  FileText,
  GitCompare,
  HeartPulse,
  Loader2,
  Plus,
  RefreshCw,
  Salad,
  Sparkles,
  Target,
  Trophy,
  Video,
  Wand2,
  X,
  Zap,
} from 'lucide-react';

import { aiExtras, athleteDevelopment, connectedTraining, headCoach, resolveEnabledAiExtras, trainingPlanner, videoAssets } from '../../lib/api';
import {
  findBlockedEquipmentDelivery,
  findPhoneGuidedWorkout,
  isEquipmentPrescription,
  phoneGuidedFallbackMessageKey,
} from '../../lib/phoneGuidedEquipmentWorkouts';
import {
  summarizeEquipmentCapabilities,
  summarizeEquipmentDeliveries,
} from '../../lib/equipmentAutomationStatus';
import {
  findSyncedMatchForPrescription,
  isAutomaticSyncedWorkoutMatch,
  isCandidateSyncedWorkoutMatch,
  summarizeSyncedWorkoutMatches,
  syncedWorkoutCompletionSourceKey,
  syncedWorkoutConfidenceKey,
  syncedWorkoutMatchStatusKey,
  syncedWorkoutSourceKey,
} from '../../lib/syncedWorkoutMatches';
import i18nInstance from '../../i18n';


const t = (key, fallback, options = {}) =>
  i18nInstance.t(key, typeof fallback === 'string' ? { defaultValue: fallback, ...options } : fallback);
const ALL_DAYS = [
  { key: 'monday', labelKey: 'planner.days.mondayShort' },
  { key: 'tuesday', labelKey: 'planner.days.tuesdayShort' },
  { key: 'wednesday', labelKey: 'planner.days.wednesdayShort' },
  { key: 'thursday', labelKey: 'planner.days.thursdayShort' },
  { key: 'friday', labelKey: 'planner.days.fridayShort' },
  { key: 'saturday', labelKey: 'planner.days.saturdayShort' },
  { key: 'sunday', labelKey: 'planner.days.sundayShort' },
];

const GOAL_SUGGESTIONS = [
  { value: 'Improve serve', labelKey: 'planner.goalSuggestions.improveServe' },
  { value: 'Increase endurance', labelKey: 'planner.goalSuggestions.increaseEndurance' },
  { value: 'Develop tactical thinking', labelKey: 'planner.goalSuggestions.developTacticalThinking' },
  { value: 'Work on net play', labelKey: 'planner.goalSuggestions.workOnNetPlay' },
  { value: 'Increase movement speed', labelKey: 'planner.goalSuggestions.increaseMovementSpeed' },
  { value: 'Strengthen backhand', labelKey: 'planner.goalSuggestions.strengthenBackhand' },
  { value: 'Reduce unforced errors', labelKey: 'planner.goalSuggestions.reduceUnforcedErrors' },
];

const RECOMMENDATION_GROUPS = [
  {
    key: 'tennis',
    titleKey: 'planner.recommendationGroups.tennis',
    icon: <Target size={18} strokeWidth={1.8} />,
    tone: 'bg-lime-50 text-lime-700 ring-lime-100',
    match: ['serve', 'backhand', 'forehand', 'volley', 'net', 'tactic', 'stroke', 'tennis', 'error'],
  },
  {
    key: 'fitness',
    titleKey: 'planner.recommendationGroups.fitness',
    icon: <Dumbbell size={18} strokeWidth={1.8} />,
    tone: 'bg-blue-50 text-blue-700 ring-blue-100',
    match: ['fitness', 'conditioning', 'speed', 'movement', 'strength', 'endurance', 'cardio'],
  },
  {
    key: 'recovery',
    titleKey: 'planner.recommendationGroups.recovery',
    icon: <HeartPulse size={18} strokeWidth={1.8} />,
    tone: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    match: ['recovery', 'mobility', 'rest', 'sleep', 'stretch'],
  },
  {
    key: 'nutrition',
    titleKey: 'planner.recommendationGroups.nutrition',
    icon: <Salad size={18} strokeWidth={1.8} />,
    tone: 'bg-amber-50 text-amber-700 ring-amber-100',
    match: ['nutrition', 'hydration', 'fuel', 'meal', 'water'],
  },
  {
    key: 'match',
    titleKey: 'planner.recommendationGroups.match',
    icon: <Trophy size={18} strokeWidth={1.8} />,
    tone: 'bg-rose-50 text-rose-700 ring-rose-100',
    match: ['match', 'point', 'serve pattern', 'return', 'pressure'],
  },
];

const VIDEO_SOURCE_TYPES = ['training', 'match', 'free_upload'];
const VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const VIDEO_UPLOAD_PHASES = {
  idle: '',
  creating_session: 'video.creatingUploadSession',
  uploading_to_storage: 'video.uploadingToPrivateStorage',
  completing_upload: 'video.completingUpload',
  completed: 'video.videoUploaded',
  failed: 'video.failedToUploadVideoFile',
};
const AUTO_ANALYSIS_DETAIL_LIMIT = 5;
const VIDEO_QUALITY_FLAGS = [
  'no_tennis_action_detected',
  'insufficient_tennis_evidence',
  'player_not_visible',
  'stroke_not_visible',
  'too_short',
  'low_visual_quality',
  'non_tennis_content',
  'camera_obstructed',
  'unsupported_video_content',
];
const VIDEO_INSUFFICIENT_RELEVANCE = new Set(['non_tennis', 'insufficient_evidence', 'tennis_context_only']);
const VIDEO_INSUFFICIENT_FLAGS = new Set([
  'no_tennis_action_detected',
  'insufficient_tennis_evidence',
  'non_tennis_content',
  'player_not_visible',
  'stroke_not_visible',
  'unsupported_video_content',
]);

const EMPTY_VIDEO_FORM = {
  title: '',
  source_type: 'free_upload',
  source_id: '',
  original_filename: '',
  mime_type: 'video/mp4',
  size_bytes: '',
  duration_sec: '',
};

const TREND_ICON = {
  increasing: '+',
  decreasing: '-',
  stable: '=',
  insufficient_data: '-',
};

const apiErrorMessage = (err, fallback, translate = (key) => key, options = {}) => {
  const detail = err?.response?.data?.detail;
  const error = err?.response?.data?.error;
  const code = detail?.code || err?.response?.data?.code;
  const status = err?.response?.status;
  const isVideoAnalysisEntitlement =
    options.videoAnalysis === true
    && status === 403
    && (
      detail?.code === 'upgrade_required'
      || detail?.feature === 'ai_video_analysis'
      || (
        error?.code === 'FORBIDDEN_ERROR'
        && error?.message === 'Feature not included in current plan'
      )
    );

  if (isVideoAnalysisEntitlement) {
    return translate('video.videoPaidOnly');
  }

  if (code === 'ai_limit_reached') {
    return translate('planner.aiAccess.limitReached');
  }

  if (code === 'upgrade_required') {
    const isVideoAnalysis = detail?.feature === 'ai_video_analysis';
    const titleKey = isVideoAnalysis ? 'video.upgradeRequired' : 'planner.aiAccess.upgradeRequired';
    const messageKey = isVideoAnalysis ? 'video.videoPaidOnly' : 'planner.aiAccess.featurePaidOnly';
    return `${translate(titleKey)}: ${translate(messageKey)}`;
  }

  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
};

const rpeTone = (rpe) => {
  if (!rpe) return 'bg-zinc-100 text-zinc-700 ring-zinc-200';
  if (rpe <= 5) return 'bg-lime-50 text-lime-700 ring-lime-100';
  if (rpe <= 7) return 'bg-amber-50 text-amber-700 ring-amber-100';
  return 'bg-rose-50 text-rose-700 ring-rose-100';
};

const videoAnalysisJobTone = (job) => {
  if (job.status === 'failed' && job.error_code === 'video_analysis_not_implemented') {
    return 'border-amber-200 bg-amber-50 text-amber-800';
  }
  if (job.status === 'completed') return 'border-lime-200 bg-lime-50 text-lime-800';
  if (job.status === 'failed') return 'border-rose-200 bg-rose-50 text-rose-800';
  if (job.status === 'queued' || job.status === 'processing') return 'border-blue-200 bg-blue-50 text-blue-800';
  return 'border-zinc-200 bg-zinc-50 text-zinc-700';
};

const videoScoreLabel = (key, translate) => translate(`video.scoreLabels.${key}`, key);

const videoFindingLabel = (group, key, translate) => translate(`video.${group}.${key}`, key);

const normalizeVideoQualityFlag = (value) =>
  String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const videoQualityFlagLabel = (flag, translate) => translate(`video.qualityFlags.${flag}`, flag);

const videoResultQualityFlags = (result) => {
  const values = [
    ...(Array.isArray(result?.analysis_quality_flags) ? result.analysis_quality_flags : []),
    ...(Array.isArray(result?.safety_flags) ? result.safety_flags : []),
  ];
  const flags = [];
  values.forEach((value) => {
    const flag = normalizeVideoQualityFlag(value);
    if (VIDEO_QUALITY_FLAGS.includes(flag) && !flags.includes(flag)) {
      flags.push(flag);
    }
  });
  return flags;
};

const videoSafetyNotes = (result, qualityFlags = []) => {
  const notes = [...qualityFlags];
  (Array.isArray(result?.safety_flags) ? result.safety_flags : []).forEach((value) => {
    const flag = normalizeVideoQualityFlag(value);
    if (VIDEO_QUALITY_FLAGS.includes(flag)) return;
    const text = String(value || '').trim();
    if (text && !notes.includes(text)) notes.push(text);
  });
  return notes;
};

const isInsufficientVideoResult = (result, qualityFlags = videoResultQualityFlags(result)) => {
  const relevance = String(result?.content_relevance || '').trim().toLowerCase();
  return VIDEO_INSUFFICIENT_RELEVANCE.has(relevance) || qualityFlags.some((flag) => VIDEO_INSUFFICIENT_FLAGS.has(flag));
};

const videoTechniqueScores = (result) =>
  result?.technique_scores && typeof result.technique_scores === 'object'
    ? Object.entries(result.technique_scores).filter(([, value]) => value !== null && value !== undefined)
    : [];

const videoTechniqueScoresAreEmptyOrZero = (scores) =>
  scores.length === 0 || scores.every(([, value]) => Number(value) === 0);

const compactVideoAdviceText = (value) => String(value || '').trim().replace(/\s+/g, ' ');

const addUniqueVideoAdvice = (items, seen, value, maxItems = 6) => {
  const text = compactVideoAdviceText(value);
  if (!text) return;
  const key = text.toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  if (items.length < maxItems) items.push(text);
};

const collectVideoFindingAdvice = (items, seen, value) => {
  if (Array.isArray(value)) {
    value.forEach((item) => collectVideoFindingAdvice(items, seen, item));
    return;
  }
  if (value && typeof value === 'object') {
    [
      'text',
      'description',
      'correction',
      'recommended_correction',
      'recommended_corrections',
      'how_to_fix',
      'focus',
      'focus_cue',
      'focus_cues',
    ].forEach((key) => collectVideoFindingAdvice(items, seen, value[key]));
    return;
  }
  addUniqueVideoAdvice(items, seen, value);
};

const videoCoachAdviceItems = (result, findings = []) => {
  const items = [];
  const seen = new Set();
  [
    result?.stroke_findings,
    result?.footwork_findings,
    result?.tactical_findings,
  ].forEach((group) => collectVideoFindingAdvice(items, seen, group));
  findings.forEach((finding) => {
    addUniqueVideoAdvice(items, seen, finding?.description);
    collectVideoFindingAdvice(items, seen, finding?.recommended_corrections);
  });
  return items;
};

const videoRecommendedDrills = (result) =>
  Array.isArray(result?.recommended_drills)
    ? result.recommended_drills.filter((drill) => {
        if (!drill || typeof drill !== 'object') return false;
        return ['name', 'purpose', 'how_to_do', 'reps_or_duration'].some((key) => compactVideoAdviceText(drill[key]));
      })
    : [];

const formatEvidenceTimestamps = (values = []) =>
  values
    .filter((value) => Number.isFinite(Number(value)))
    .map((value) => `${Number(value)} ms`)
    .join(', ');

const responseList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const blockedUploadHeaderNames = new Set([
  'auth' + 'orization',
  'coo' + 'kie',
  'host',
  'content-length',
  'x' + '-csrf' + '-token',
  'x' + '-request' + '-id',
]);

const buildPrivateUploadHeaders = (uploadHeaders = {}, file, fallbackMimeType) => {
  const headers = new Headers();
  Object.entries(uploadHeaders || {}).forEach(([key, value]) => {
    const normalizedKey = String(key || '').trim();
    if (!normalizedKey || value === undefined || value === null) return;
    if (blockedUploadHeaderNames.has(normalizedKey.toLowerCase())) return;
    headers.set(normalizedKey, String(value));
  });

  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', file.type || fallbackMimeType || 'video/mp4');
  }
  return headers;
};

const buildUploadCompletionMetadata = (file, formState) => {
  const durationSec = Number(formState.duration_sec) || undefined;
  return {
    size_bytes: file?.size,
    duration_sec: durationSec,
    width: undefined,
    height: undefined,
  };
};

const urgencyTone = (urgency) => {
  if (urgency === 'low') return 'border-lime-200 bg-lime-50 text-lime-800';
  if (urgency === 'medium') return 'border-amber-200 bg-amber-50 text-amber-800';
  if (urgency === 'high') return 'border-rose-200 bg-rose-50 text-rose-800';
  return 'border-zinc-200 bg-zinc-50 text-zinc-700';
};

const recommendationGroupFor = (text = '') => {
  const normalized = text.toLowerCase();
  return (
    RECOMMENDATION_GROUPS.find((group) =>
      group.match.some((term) => normalized.includes(term)),
    ) || RECOMMENDATION_GROUPS[0]
  );
};

const groupedPlanItems = (items = []) =>
  items.reduce((groups, item) => {
    const haystack = `${item.focus || ''} ${(item.exercises || []).join(' ')} ${item.notes || ''}`;
    const group = recommendationGroupFor(haystack);
    return {
      ...groups,
      [group.key]: [...(groups[group.key] || []), item],
    };
  }, {});

const displayGoalLabel = (goal, translate) => {
  const suggestion = GOAL_SUGGESTIONS.find((item) => item.value === goal);
  return suggestion ? translate(suggestion.labelKey) : goal;
};

const translatedEnumLabel = (namespace, value, translate) => {
  if (!value) return '';
  const key = `${namespace}.${value}`;
  const translated = translate(key);
  return translated === key ? value : translated;
};

const developmentSignalLabel = (item) => {
  if (!item) return null;
  if (typeof item === 'string') return item;
  if (typeof item === 'number') return String(item);
  return item.label || item.code || item.title || item.category || null;
};

const developmentSignalList = (items, limit = 3) =>
  (Array.isArray(items) ? items : []).map(developmentSignalLabel).filter(Boolean).slice(0, limit);

const formatDevelopmentDate = (value, lang) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(
    lang === 'ru' ? 'ru-RU' : lang === 'fr' ? 'fr-FR' : 'en-GB',
    { day: '2-digit', month: 'short', year: 'numeric' },
  );
};

const formatVideoDate = (value, lang) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(
    lang === 'ru' ? 'ru-RU' : lang === 'fr' ? 'fr-FR' : 'en-GB',
    { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' },
  );
};

const SourceMeta = ({ source, fallbackReason }) => {
  if (!source && !fallbackReason) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2 text-xs">
      {source ? (
        <span className={`rounded-full px-2.5 py-1 font-bold ring-1 ${
          source === 'openai'
            ? 'bg-lime-50 text-lime-700 ring-lime-100'
            : 'bg-amber-50 text-amber-700 ring-amber-100'
        }`}>
          {t('planner.sourceLabel')}: {source}
        </span>
      ) : null}
      {fallbackReason ? (
        <span className="rounded-full bg-zinc-100 px-2.5 py-1 font-bold text-zinc-600 ring-1 ring-zinc-200">
          {t('planner.fallbackLabel')}: {fallbackReason}
        </span>
      ) : null}
    </div>
  );
};

const Panel = ({ children, className = '' }) => (
  <section className={`rounded-lg border border-zinc-200 bg-white shadow-sm ${className}`}>
    {children}
  </section>
);

const SectionHeader = ({ icon, kicker, title, body, action }) => (
  <div className="mb-5 flex items-start justify-between gap-4">
    <div className="flex min-w-0 items-start gap-3">
      {icon ? (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-zinc-950 text-lime-300">
          {icon}
        </div>
      ) : null}
      <div className="min-w-0">
        {kicker ? (
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
            {kicker}
          </p>
        ) : null}
        <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">{title}</h2>
        {body ? <p className="mt-1 text-sm leading-6 text-zinc-500">{body}</p> : null}
      </div>
    </div>
    {action}
  </div>
);

const PrimaryButton = ({ children, className = '', disabled, ...props }) => (
  <button
    type="button"
    disabled={disabled}
    className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
    {...props}
  >
    {children}
  </button>
);

const SecondaryButton = ({ children, className = '', disabled, ...props }) => (
  <button
    type="button"
    disabled={disabled}
    className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 text-sm font-black text-zinc-700 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
    {...props}
  >
    {children}
  </button>
);

const VideoAnalysisFoundationPanel = ({ language, t }) => {
  const [videos, setVideos] = useState([]);
  const [jobsByVideoId, setJobsByVideoId] = useState({});
  const [resultsByVideoId, setResultsByVideoId] = useState({});
  const [findingsByVideoId, setFindingsByVideoId] = useState({});
  const [analysisDetailsLoadedByVideoId, setAnalysisDetailsLoadedByVideoId] = useState({});
  const [analysisDetailsLoadingByVideoId, setAnalysisDetailsLoadingByVideoId] = useState({});
  const [analysisDetailsErrorsByVideoId, setAnalysisDetailsErrorsByVideoId] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [form, setForm] = useState(EMPTY_VIDEO_FORM);
  const [selectedFile, setSelectedFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [pendingSession, setPendingSession] = useState(null);
  const [actionId, setActionId] = useState('');
  const [uploadPhase, setUploadPhase] = useState('idle');
  const [showVideoHistory, setShowVideoHistory] = useState(false);

  const loadVideoAnalysisDetails = async (asset) => {
    if (!asset?.id) return;
    setAnalysisDetailsLoadingByVideoId((prev) => ({ ...prev, [asset.id]: true }));
    setAnalysisDetailsErrorsByVideoId((prev) => ({ ...prev, [asset.id]: false }));
    let detailFailed = false;
    let jobs = [];
    let results = [];
    let findings = [];

    try {
      const jobsResponse = await videoAssets.getAnalysisJobs(asset.id);
      jobs = responseList(jobsResponse.data);
    } catch (_err) {
      detailFailed = true;
    }
    setJobsByVideoId((prev) => ({ ...prev, [asset.id]: jobs }));

    const hasCompletedJob = jobs.some((job) => job.status === 'completed') || asset.analysis_status === 'completed';
    if (hasCompletedJob) {
      try {
        const resultResponse = await videoAssets.getAnalysisResults(asset.id, { limit: 1 });
        results = responseList(resultResponse.data);
      } catch (_err) {
        detailFailed = true;
      }
    }
    setResultsByVideoId((prev) => ({ ...prev, [asset.id]: results }));

    if (results[0]) {
      try {
        const findingResponse = await videoAssets.getTechniqueFindings(asset.id, { limit: 20 });
        findings = responseList(findingResponse.data);
      } catch (_err) {
        detailFailed = true;
      }
    }
    setFindingsByVideoId((prev) => ({ ...prev, [asset.id]: findings }));
    setAnalysisDetailsLoadedByVideoId((prev) => ({ ...prev, [asset.id]: true }));
    setAnalysisDetailsErrorsByVideoId((prev) => ({ ...prev, [asset.id]: detailFailed }));
    setAnalysisDetailsLoadingByVideoId((prev) => ({ ...prev, [asset.id]: false }));
  };

  const loadVideos = async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const { data } = await videoAssets.getAll({ limit: 20 });
      const rows = responseList(data);
      setVideos(rows);
      setJobsByVideoId({});
      setResultsByVideoId({});
      setFindingsByVideoId({});
      setAnalysisDetailsLoadedByVideoId({});
      setAnalysisDetailsLoadingByVideoId({});
      setAnalysisDetailsErrorsByVideoId({});

      for (const asset of rows.slice(0, AUTO_ANALYSIS_DETAIL_LIMIT)) {
        await loadVideoAnalysisDetails(asset);
      }
    } catch (_err) {
      setLoadFailed(true);
      toast.error(t('video.failedToLoadVideos'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFormField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (file.type && !VIDEO_MIME_TYPES.includes(file.type)) {
      setSelectedFile(null);
      setUploadPhase('failed');
      toast.error(t('video.failedToUploadVideoFile'));
      return;
    }
    const mimeType = file.type || 'video/mp4';
    setSelectedFile(file);
    setForm((prev) => ({
      ...prev,
      title: prev.title.trim() ? prev.title : file.name.replace(/\.[^.]+$/, ''),
      original_filename: file.name,
      mime_type: mimeType,
      size_bytes: String(file.size),
    }));
    setUploadPhase('idle');
  };

  const buildUploadPayload = () => {
    const payload = {
      title: form.title.trim(),
      source_type: form.source_type,
      original_filename: form.original_filename.trim(),
      mime_type: form.mime_type,
      size_bytes: Number(form.size_bytes),
    };
    if (form.source_id.trim()) payload.source_id = form.source_id.trim();
    if (form.duration_sec !== '') payload.duration_sec = Number(form.duration_sec);
    return payload;
  };

  const completeUploadSession = async (sessionId, metadata = buildUploadCompletionMetadata(selectedFile, form)) => {
    await videoAssets.completeUploadSession(sessionId, metadata);
  };

  const uploadFileToSignedUrl = async (uploadSession, file) => {
    const response = await fetch(uploadSession.upload_url, {
      method: 'PUT',
      body: file,
      headers: buildPrivateUploadHeaders(uploadSession.upload_headers, file, form.mime_type),
      credentials: 'omit',
      mode: 'cors',
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error(`Private upload failed with status ${response.status}`);
    }
  };

  const handleCreateUploadSession = async () => {
    const file = selectedFile;
    setCreating(true);
    setUploadPhase('creating_session');
    let currentPhase = 'creating_session';
    try {
      const response = await videoAssets.createUploadSession(buildUploadPayload());
      const bundle = response?.data || response || {};
      const asset = bundle.asset || null;
      const uploadSession = bundle.upload_session || null;
      if (!asset || !uploadSession) {
        throw new Error('Upload session response is invalid');
      }
      setPendingSession(uploadSession);
      toast.success(t('video.uploadSessionCreated'));

      if (uploadSession.upload_method === 'presigned_put') {
        currentPhase = 'uploading_to_storage';
        if (!uploadSession.upload_url) {
          throw new Error('Upload session response is invalid');
        }
        if (!file) {
          throw new Error('Selected video file is required for private upload');
        }
        toast.success(t('video.signedUploadUrlReceived'));
        setUploadPhase('uploading_to_storage');
        await uploadFileToSignedUrl(uploadSession, file);
        currentPhase = 'completing_upload';
        setUploadPhase('completing_upload');
        await completeUploadSession(uploadSession.id, buildUploadCompletionMetadata(file, form));
        setPendingSession(null);
        setSelectedFile(null);
        setForm(EMPTY_VIDEO_FORM);
        setUploadPhase('completed');
        toast.success(t('video.videoUploaded'));
      } else {
        setSelectedFile(null);
        setForm(EMPTY_VIDEO_FORM);
        setUploadPhase('idle');
      }
      await loadVideos();
    } catch (err) {
      setUploadPhase('failed');
      const fallback =
        err?.message === 'Upload session response is invalid'
          ? t('video.invalidUploadSessionResponse')
          : currentPhase === 'uploading_to_storage'
          ? t('video.failedToUploadVideoFile')
          : currentPhase === 'completing_upload'
            ? t('video.failedToCompleteUpload')
            : t('video.failedToCreateUploadSession');
      toast.error(apiErrorMessage(err, fallback, t));
    } finally {
      setCreating(false);
    }
  };

  const handleCompleteUpload = async () => {
    if (!pendingSession?.id) return;
    setActionId(`complete-${pendingSession.id}`);
    setUploadPhase('completing_upload');
    try {
      await completeUploadSession(pendingSession.id);
      setPendingSession(null);
      setUploadPhase('completed');
      toast.success(t('video.uploadComplete'));
      await loadVideos();
    } catch (err) {
      setUploadPhase('failed');
      toast.error(apiErrorMessage(err, t('video.failedToCompleteUpload'), t));
    } finally {
      setActionId('');
    }
  };

  const handleRequestAnalysis = async (asset) => {
    setActionId(`analysis-${asset.id}`);
    try {
      await videoAssets.createAnalysisJob(asset.id, {});
      toast.success(t('video.analysisRequested'));
      await loadVideos();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('video.failedToRequestAnalysis'), t, { videoAnalysis: true }));
    } finally {
      setActionId('');
    }
  };

  const latestVideo = videos[0] || null;
  const activeAnalysisStatuses = new Set(['queued', 'pending', 'processing', 'running', 'in_progress']);
  const activeAnalysisCount = videos.filter((asset) => activeAnalysisStatuses.has(String(asset?.analysis_status || '').toLowerCase())).length;
  const latestVideoRequestLoading = latestVideo ? actionId === `analysis-${latestVideo.id}` : false;

  return (
    <Panel className="p-5">
      <SectionHeader
        icon={<Video size={20} strokeWidth={1.8} />}
        kicker={t('video.videoAnalysis')}
        title={t('video.videoAnalysisFoundation')}
        body={t('video.realUploadUnavailable')}
        action={
          <SecondaryButton onClick={loadVideos} disabled={loading} className="min-h-10 px-3">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {t('common.retry')}
          </SecondaryButton>
        }
      />

      {loadFailed ? (
        <div className="mb-4 rounded-lg bg-rose-50 p-3 text-sm font-bold text-rose-700 ring-1 ring-rose-100">
          {t('video.failedToLoadVideos')}
        </div>
      ) : null}

      <div className="mb-4 rounded-lg border border-zinc-200 bg-white p-4">
        <div className="grid gap-2 sm:grid-cols-3">
          <ConnectedSummaryTile
            label={t('video.videoAnalysisHistoryCount')}
            value={connectedNumber(videos.length, 0)}
          />
          <ConnectedSummaryTile
            label={t('video.activeVideoAnalysisCount')}
            value={connectedNumber(activeAnalysisCount, 0)}
            tone={activeAnalysisCount ? 'bg-sky-50 text-sky-800 ring-sky-100' : 'bg-zinc-50 text-zinc-600 ring-zinc-100'}
          />
          <ConnectedSummaryTile
            label={t('video.latestVideoAnalysis')}
            value={latestVideo ? `${translatedEnumLabel('video.statuses', latestVideo.analysis_status, t)} / ${formatVideoDate(latestVideo.created_at, language)}` : t('video.noVideosYet')}
            tone={latestVideo?.analysis_status === 'completed' ? 'bg-lime-50 text-lime-800 ring-lime-100' : 'bg-zinc-50 text-zinc-600 ring-zinc-100'}
          />
        </div>
        <p className="mt-3 text-sm font-semibold leading-6 text-zinc-600">
          {t('video.videoAnalysisSummaryBody')}
        </p>
        {latestVideo ? (
          <div className="mt-3 rounded-lg bg-zinc-50 p-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
                  {t('video.latestVideoAnalysis')}
                </p>
                <h3 className="mt-1 truncate font-black text-zinc-950">{latestVideo.title}</h3>
                <p className="mt-1 text-xs font-bold text-zinc-500">
                  {t(`video.sourceTypes.${latestVideo.source_type}`, latestVideo.source_type)} / {formatVideoDate(latestVideo.created_at, language)}
                </p>
              </div>
              {latestVideo.upload_status === 'completed' ? (
                <SecondaryButton onClick={() => handleRequestAnalysis(latestVideo)} disabled={latestVideoRequestLoading} className="min-h-10 px-3">
                  {latestVideoRequestLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {t('video.requestAnalysis')}
                </SecondaryButton>
              ) : null}
            </div>
            <div className="mt-3 grid gap-2 text-xs font-bold sm:grid-cols-2">
              <span className="rounded-lg bg-white px-3 py-2 text-zinc-600 ring-1 ring-zinc-100">
                {t('video.uploadStatus')}: {translatedEnumLabel('video.statuses', latestVideo.upload_status, t)}
              </span>
              <span className="rounded-lg bg-white px-3 py-2 text-zinc-600 ring-1 ring-zinc-100">
                {t('video.analysisStatus')}: {translatedEnumLabel('video.statuses', latestVideo.analysis_status, t)}
              </span>
            </div>
          </div>
        ) : (
          <div className="mt-3 rounded-lg bg-zinc-50 p-3 text-sm font-bold text-zinc-500">
            {t('video.noVideosYet')}
          </div>
        )}
      </div>

      <div className="rounded-lg bg-zinc-50 p-4">
        <p className="font-black text-zinc-950">{t('video.createUploadSession')}</p>
        <div className="mt-3 grid gap-3">
          <label className="grid gap-1 text-xs font-black text-zinc-500">
            {t('video.selectVideoFile')}
            <input
              type="file"
              accept={VIDEO_MIME_TYPES.join(',')}
              onChange={handleFileSelect}
              className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-bold text-zinc-950 file:mr-3 file:rounded-lg file:border-0 file:bg-lime-100 file:px-3 file:py-2 file:text-sm file:font-black file:text-lime-800"
            />
            {selectedFile ? (
              <span className="text-xs font-bold text-zinc-500">
                {t('video.uploadToPrivateStorage')}: {selectedFile.name}
              </span>
            ) : null}
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(event) => setFormField('title', event.target.value)}
            placeholder={t('video.title')}
            className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1 text-xs font-black text-zinc-500">
              {t('video.sourceType')}
              <select
                value={form.source_type}
                onChange={(event) => setFormField('source_type', event.target.value)}
                className="min-h-11 rounded-lg border border-zinc-200 bg-white px-3 text-sm font-bold text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              >
                {VIDEO_SOURCE_TYPES.map((sourceType) => (
                  <option key={sourceType} value={sourceType}>
                    {t(`video.sourceTypes.${sourceType}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-xs font-black text-zinc-500">
              {t('video.sourceId')}
              <input
                type="text"
                value={form.source_id}
                onChange={(event) => setFormField('source_id', event.target.value)}
                className="min-h-11 rounded-lg border border-zinc-200 bg-white px-3 text-sm font-bold text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              />
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1 text-xs font-black text-zinc-500">
              {t('video.originalFilename')}
              <input
                type="text"
                value={form.original_filename}
                onChange={(event) => setFormField('original_filename', event.target.value)}
                className="min-h-11 rounded-lg border border-zinc-200 bg-white px-3 text-sm font-bold text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              />
            </label>
            <label className="grid gap-1 text-xs font-black text-zinc-500">
              {t('video.mimeType')}
              <select
                value={form.mime_type}
                onChange={(event) => setFormField('mime_type', event.target.value)}
                className="min-h-11 rounded-lg border border-zinc-200 bg-white px-3 text-sm font-bold text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              >
                {VIDEO_MIME_TYPES.map((mimeType) => (
                  <option key={mimeType} value={mimeType}>{mimeType}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1 text-xs font-black text-zinc-500">
              {t('video.sizeBytes')}
              <input
                type="number"
                min={0}
                value={form.size_bytes}
                onChange={(event) => setFormField('size_bytes', event.target.value)}
                className="min-h-11 rounded-lg border border-zinc-200 bg-white px-3 text-sm font-bold text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              />
            </label>
            <label className="grid gap-1 text-xs font-black text-zinc-500">
              {t('video.durationSeconds')}
              <input
                type="number"
                min={0}
                value={form.duration_sec}
                onChange={(event) => setFormField('duration_sec', event.target.value)}
                className="min-h-11 rounded-lg border border-zinc-200 bg-white px-3 text-sm font-bold text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              />
            </label>
          </div>
          <SecondaryButton onClick={handleCreateUploadSession} disabled={creating || !selectedFile || !form.title.trim() || !form.original_filename.trim() || form.size_bytes === ''}>
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {t('video.createUploadSession')}
          </SecondaryButton>
          {uploadPhase !== 'idle' ? (
            <div className={`rounded-lg px-3 py-2 text-sm font-black ring-1 ${
              uploadPhase === 'failed'
                ? 'bg-rose-50 text-rose-700 ring-rose-100'
                : uploadPhase === 'completed'
                  ? 'bg-lime-50 text-lime-700 ring-lime-100'
                  : 'bg-sky-50 text-sky-700 ring-sky-100'
            }`}>
              {uploadPhase === 'uploading_to_storage' ? <Loader2 size={14} className="mr-2 inline animate-spin" /> : null}
              {t(VIDEO_UPLOAD_PHASES[uploadPhase])}
            </div>
          ) : null}
        </div>
      </div>

      {pendingSession ? (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-black">
                {pendingSession.upload_method === 'presigned_put' ? t('video.privateUpload') : t('video.uploadMetadata')}
              </p>
              <p className="mt-1 font-bold">
                {pendingSession.upload_method === 'presigned_put'
                  ? t('video.signedUploadUrlReceived')
                  : `${t('video.deferredUpload')}: ${t('video.realUploadUnavailable')}`}
              </p>
            </div>
            {pendingSession.upload_method === 'deferred' ? (
              <SecondaryButton onClick={handleCompleteUpload} disabled={actionId === `complete-${pendingSession.id}`}>
                {actionId === `complete-${pendingSession.id}` ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                {t('video.markUploadComplete')}
              </SecondaryButton>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="mt-5">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-black text-zinc-950">{t('video.videoAnalysisSummaryTitle')}</p>
            <p className="mt-1 text-sm font-semibold leading-6 text-zinc-500">
              {t('video.videoHistoryCollapsedHint')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {loading ? <Loader2 size={16} className="animate-spin text-zinc-400" /> : null}
            <SecondaryButton onClick={() => setShowVideoHistory((current) => !current)} className="min-h-10 px-3">
              {showVideoHistory ? <ChevronUp size={14} strokeWidth={1.8} /> : <ChevronDown size={14} strokeWidth={1.8} />}
              {showVideoHistory ? t('video.hideVideoAnalysisHistory') : t('video.showVideoAnalysisHistory')}
            </SecondaryButton>
          </div>
        </div>
        {!showVideoHistory && videos.length === 0 && !loading ? (
          <div className="rounded-lg bg-zinc-50 p-4 text-sm font-bold text-zinc-500">
            {t('video.noVideosYet')}
          </div>
        ) : null}
        {showVideoHistory ? videos.length === 0 && !loading ? (
          <div className="rounded-lg bg-zinc-50 p-4 text-sm font-bold text-zinc-500">
            {t('video.noVideosYet')}
          </div>
        ) : (
          <div className="space-y-3">
            {videos.map((asset) => {
              const jobs = jobsByVideoId[asset.id] || [];
              const latestResult = (resultsByVideoId[asset.id] || [])[0];
              const findings = findingsByVideoId[asset.id] || [];
              const detailsLoaded = Boolean(analysisDetailsLoadedByVideoId[asset.id]);
              const detailsLoading = Boolean(analysisDetailsLoadingByVideoId[asset.id]);
              const detailsFailed = Boolean(analysisDetailsErrorsByVideoId[asset.id]);
              const qualityFlags = videoResultQualityFlags(latestResult);
              const safetyNotes = videoSafetyNotes(latestResult, qualityFlags);
              const insufficientContent = isInsufficientVideoResult(latestResult, qualityFlags);
              const techniqueScores = videoTechniqueScores(latestResult);
              const hideTechniqueScores = insufficientContent && videoTechniqueScoresAreEmptyOrZero(techniqueScores);
              const coachAdviceItems = !insufficientContent ? videoCoachAdviceItems(latestResult, findings) : [];
              const recommendedDrills = !insufficientContent ? videoRecommendedDrills(latestResult) : [];
              const requestLoading = actionId === `analysis-${asset.id}`;
              return (
                <article key={asset.id} className="rounded-lg border border-zinc-200 bg-white p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h3 className="truncate font-black text-zinc-950">{asset.title}</h3>
                      <p className="mt-1 text-xs font-bold text-zinc-500">
                        {t(`video.sourceTypes.${asset.source_type}`, asset.source_type)} / {formatVideoDate(asset.created_at, language)}
                      </p>
                    </div>
                    {asset.upload_status === 'completed' ? (
                      <SecondaryButton onClick={() => handleRequestAnalysis(asset)} disabled={requestLoading}>
                        {requestLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        {t('video.requestAnalysis')}
                      </SecondaryButton>
                    ) : null}
                  </div>
                  <div className="mt-3 grid gap-2 text-xs font-bold sm:grid-cols-2">
                    <span className="rounded-lg bg-zinc-50 px-3 py-2 text-zinc-600">
                      {t('video.uploadStatus')}: {translatedEnumLabel('video.statuses', asset.upload_status, t)}
                    </span>
                    <span className="rounded-lg bg-zinc-50 px-3 py-2 text-zinc-600">
                      {t('video.analysisStatus')}: {translatedEnumLabel('video.statuses', asset.analysis_status, t)}
                    </span>
                  </div>
                  <div className="mt-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">{t('video.analysisJobs')}</p>
                      {detailsFailed || (!detailsLoaded && !detailsLoading) ? (
                        <SecondaryButton
                          onClick={() => loadVideoAnalysisDetails(asset)}
                          disabled={detailsLoading}
                          className="min-h-9 px-3 text-xs"
                        >
                          {detailsLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                          {t('video.loadAnalysisDetails')}
                        </SecondaryButton>
                      ) : null}
                    </div>
                    {detailsFailed ? (
                      <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800 ring-1 ring-amber-100">
                        {t('video.failedToLoadAnalysisResults')}
                      </p>
                    ) : null}
                    {detailsLoading && !detailsLoaded ? (
                      <p className="mt-2 rounded-lg bg-sky-50 px-3 py-2 text-xs font-bold text-sky-700 ring-1 ring-sky-100">
                        <Loader2 size={14} className="mr-2 inline animate-spin" />
                        {t('video.loadingAnalysisDetails')}
                      </p>
                    ) : null}
                    {!detailsLoaded && !detailsLoading ? (
                      <p className="mt-2 rounded-lg bg-zinc-50 px-3 py-2 text-xs font-bold text-zinc-500">
                        {t('video.analysisDetailsNotLoaded')}
                      </p>
                    ) : null}
                    {detailsLoaded && jobs.length === 0 ? (
                      <p className="mt-2 rounded-lg bg-zinc-50 px-3 py-2 text-xs font-bold text-zinc-500">
                        {t('video.statuses.not_requested')}
                      </p>
                    ) : detailsLoaded ? (
                      <div className="mt-2 space-y-2">
                        {jobs.slice(0, 3).map((job) => {
                          const isNotImplemented = job.status === 'failed' && job.error_code === 'video_analysis_not_implemented';
                          return (
                            <div key={job.id} className={`rounded-lg border px-3 py-2 text-xs ${videoAnalysisJobTone(job)}`}>
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="font-black">{translatedEnumLabel('video.statuses', job.status, t)}</span>
                                <span className="font-bold">{job.provider} / {job.model}</span>
                              </div>
                              <p className="mt-1 font-bold">
                                {formatVideoDate(job.requested_at, language)}
                                {job.completed_at ? ` / ${formatVideoDate(job.completed_at, language)}` : ''}
                              </p>
                              {isNotImplemented ? (
                                <p className="mt-1 font-bold">{t('video.analysisNotImplemented')}</p>
                              ) : job.error_code || job.error_message ? (
                                <p className="mt-1 font-bold text-rose-700">
                                  {job.error_code || ''}{job.error_message ? `: ${job.error_message}` : ''}
                                </p>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                    {detailsLoaded && latestResult ? (
                      <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-700">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="font-black text-zinc-900">{t('video.analysisResult')}</p>
                          <div className="flex flex-wrap gap-2 font-bold text-zinc-500">
                            {latestResult.confidence ? (
                              <span>{t('video.confidence')}: {videoFindingLabel('confidenceLabels', latestResult.confidence, t)}</span>
                            ) : null}
                            {latestResult.model_version ? <span>{t('video.model')}: {latestResult.model_version}</span> : null}
                            {latestResult.created_at ? <span>{t('video.created')}: {formatVideoDate(latestResult.created_at, language)}</span> : null}
                          </div>
                        </div>
                        {latestResult.summary ? (
                          <>
                            <p className="mt-2 font-black text-zinc-700">{t('video.analysisSummary')}</p>
                            <p className="mt-1 font-bold text-zinc-600">{latestResult.summary}</p>
                          </>
                        ) : null}
                        {!insufficientContent ? (
                          <div className="mt-3 rounded-lg bg-white p-3 ring-1 ring-zinc-100">
                            <p className="font-black text-zinc-900">{t('video.coachAdvice')}</p>
                            {coachAdviceItems.length > 0 ? (
                              <div className="mt-2">
                                <p className="font-black text-zinc-700">{t('video.actionableGuidance')}</p>
                                <ul className="mt-2 space-y-1 font-bold text-zinc-600">
                                  {coachAdviceItems.map((item) => (
                                    <li key={item} className="flex gap-2">
                                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-lime-500" />
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                            {recommendedDrills.length > 0 ? (
                              <div className="mt-3">
                                <p className="font-black text-zinc-700">{t('video.recommendedDrills')}</p>
                                <div className="mt-2 space-y-2">
                                  {recommendedDrills.map((drill, index) => {
                                    const name = compactVideoAdviceText(drill.name) || `${t('video.trainingDrill')} ${index + 1}`;
                                    const purpose = compactVideoAdviceText(drill.purpose);
                                    const howToDo = compactVideoAdviceText(drill.how_to_do);
                                    const repsOrDuration = compactVideoAdviceText(drill.reps_or_duration);
                                    return (
                                      <div key={`${name}-${index}`} className="rounded-lg bg-zinc-50 px-3 py-2 font-bold text-zinc-600 ring-1 ring-zinc-100">
                                        <p className="text-zinc-900">{name}</p>
                                        {purpose ? <p className="mt-1">{purpose}</p> : null}
                                        {howToDo ? <p className="mt-1"><span className="text-zinc-900">{t('video.howToDo')}:</span> {howToDo}</p> : null}
                                        {repsOrDuration ? <p className="mt-1"><span className="text-zinc-900">{t('video.repsOrDuration')}:</span> {repsOrDuration}</p> : null}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : null}
                            {coachAdviceItems.length === 0 && recommendedDrills.length === 0 ? (
                              <p className="mt-2 font-bold text-zinc-500">{t('video.noCoachAdviceYet')}</p>
                            ) : null}
                          </div>
                        ) : null}
                        <p className="mt-2 rounded-lg bg-white px-3 py-2 font-bold text-zinc-500 ring-1 ring-zinc-100">
                          {t('video.analysisTrainingGuidanceOnly')}
                        </p>
                        {insufficientContent ? (
                          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900">
                            <p className="font-black">{t('video.insufficientContentWarning')}</p>
                            <p className="mt-1 font-bold">{t('video.uploadBetterTennisVideoGuidance')}</p>
                          </div>
                        ) : null}
                        {hideTechniqueScores ? (
                          <p className="mt-3 rounded-lg bg-white px-3 py-2 font-bold text-zinc-500 ring-1 ring-zinc-100">
                            {t('video.technicalScoresUnavailable')}
                          </p>
                        ) : techniqueScores.length > 0 ? (
                          <div className="mt-3">
                            <p className="font-black text-zinc-700">{t('video.techniqueScores')}</p>
                            <div className="mt-2 grid gap-2 sm:grid-cols-2">
                              {techniqueScores.map(([key, value]) => (
                                <div key={key} className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 font-bold text-zinc-600 ring-1 ring-zinc-100">
                                  <span>{videoScoreLabel(key, t)}</span>
                                  <span className="text-zinc-900">{value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        {safetyNotes.length > 0 ? (
                          <div className="mt-3">
                            <p className="font-black text-zinc-700">{t('video.safetyNotes')}</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {safetyNotes.map((flag, index) => (
                                <span key={`${flag}-${index}`} className="rounded-full bg-white px-2 py-1 font-bold text-zinc-600 ring-1 ring-zinc-100">
                                  {VIDEO_QUALITY_FLAGS.includes(flag) ? videoQualityFlagLabel(flag, t) : flag}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        <div className="mt-3">
                          <p className="font-black text-zinc-700">{t('video.techniqueFindings')}</p>
                          {findings.length === 0 ? (
                            <p className="mt-2 rounded-lg bg-white px-3 py-2 font-bold text-zinc-500 ring-1 ring-zinc-100">
                              {t('video.noTechniqueFindingsYet')}
                            </p>
                          ) : (
                            <div className="mt-2 space-y-2">
                              {findings.slice(0, 5).map((finding) => {
                                const findingMeta = [
                                  finding.confidence ? `${t('video.confidence')}: ${videoFindingLabel('confidenceLabels', finding.confidence, t)}` : '',
                                  Array.isArray(finding.evidence_timestamps) && finding.evidence_timestamps.length > 0
                                    ? `${t('video.evidenceTimestamps')}: ${formatEvidenceTimestamps(finding.evidence_timestamps)}`
                                    : '',
                                ].filter(Boolean).join(' / ');

                                return (
                                  <div key={finding.id} className="rounded-lg bg-white px-3 py-2 font-bold text-zinc-600 ring-1 ring-zinc-100">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <span className="text-zinc-900">{finding.title}</span>
                                      <span>
                                        {videoFindingLabel('findingCategories', finding.category, t)}
                                        {finding.severity ? ` / ${t('video.severity')}: ${videoFindingLabel('severityLabels', finding.severity, t)}` : ''}
                                      </span>
                                    </div>
                                    {finding.description ? <p className="mt-1">{finding.description}</p> : null}
                                    {findingMeta ? <p className="mt-1 text-zinc-500">{findingMeta}</p> : null}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : detailsLoaded ? (
                      <p className="mt-2 rounded-lg bg-zinc-50 px-3 py-2 text-xs font-bold text-zinc-500">
                        {t('video.noAnalysisResultsYet')}
                      </p>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </div>
    </Panel>
  );
};

const DevelopmentProfileContextPanel = ({ profile, loading, loadFailed, language, t }) => {
  const focusAreas = developmentSignalList(profile?.next_focus_areas);
  const dataQualityFlags = developmentSignalList(profile?.data_quality_flags, 4);
  const updatedAt = formatDevelopmentDate(profile?.updated_at || profile?.generated_at, language);
  const contextAvailable = Boolean(profile) && !loadFailed;

  return (
    <Panel className="p-5">
      <SectionHeader
        icon={<BrainCircuit size={20} strokeWidth={1.8} />}
        kicker={t('profile.development.usedByPlanner', 'Used by AI Planner')}
        title={t('profile.development.contextTitle', 'Development profile context')}
        body={t('profile.development.safetyNote', 'This profile is for training guidance only and is not medical advice.')}
      />

      {loading ? (
        <div className="grid gap-3">
          <div className="h-14 animate-pulse rounded-lg bg-zinc-100" />
          <div className="h-14 animate-pulse rounded-lg bg-zinc-100" />
        </div>
      ) : loadFailed ? (
        <div className="rounded-lg bg-rose-50 p-3 text-sm font-bold text-rose-700 ring-1 ring-rose-100">
          {t('profile.development.loadError', 'Failed to load development profile')}
        </div>
      ) : contextAvailable ? (
        <div className="space-y-4">
          <div className="grid gap-2 text-sm">
            <div className="flex items-center justify-between gap-3 rounded-lg bg-zinc-50 px-3 py-2">
              <span className="font-bold text-zinc-500">{t('profile.development.lastUpdated', 'Last updated')}</span>
              <span className="text-right font-black text-zinc-950">{updatedAt || t('common.notSet', 'Not set')}</span>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg bg-lime-50 px-3 py-2 text-lime-800 ring-1 ring-lime-100">
              <span className="font-bold">{t('profile.development.guidanceOnly', 'Training guidance only')}</span>
              <span className="font-black">{t('common.available', 'Available')}</span>
            </div>
          </div>

          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
              {t('profile.development.focusAreas', 'Focus areas')}
            </p>
            {focusAreas.length ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {focusAreas.map((item) => (
                  <span key={item} className="rounded-full bg-lime-50 px-3 py-1 text-xs font-bold text-lime-700 ring-1 ring-lime-100">
                    {item}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-2 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold text-zinc-500">
                {t('profile.development.profileNotReady', 'Profile not ready yet')}
              </p>
            )}
          </div>

          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
              {t('profile.development.dataQuality', 'Data quality')}
            </p>
            <p className="mt-2 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold text-zinc-600">
              {dataQualityFlags.length ? dataQualityFlags.join(' · ') : t('common.notSet', 'Not set')}
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg bg-zinc-50 p-4">
          <p className="font-black text-zinc-950">{t('profile.development.profileNotReady', 'Profile not ready yet')}</p>
          <p className="mt-2 text-sm leading-6 text-zinc-500">
            {t('profile.development.improveData', 'Add more training, match, wearable, recovery, and nutrition data to improve this profile.')}
          </p>
        </div>
      )}
    </Panel>
  );
};

const asHeadCoachArray = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item !== null && item !== undefined);
  if (value === null || value === undefined || value === '') return [];
  return [value];
};

const compactHeadCoachText = (value) => String(value ?? '').trim().replace(/\s+/g, ' ');

const formatHeadCoachFieldLabel = (key) =>
  compactHeadCoachText(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const headCoachItemTitle = (item) => {
  if (typeof item !== 'object' || item === null) return compactHeadCoachText(item);
  return compactHeadCoachText(
    item.title ||
      item.name ||
      item.area ||
      item.recommendation ||
      item.action ||
      item.text ||
      item.summary ||
      item.description,
  );
};

const formatHeadCoachValue = (value) => {
  if (Array.isArray(value)) return value.map(formatHeadCoachValue).filter(Boolean).join(', ');
  if (typeof value === 'object' && value !== null) {
    const title = headCoachItemTitle(value);
    if (title) return title;
    return Object.entries(value)
      .map(([key, nestedValue]) => {
        const formatted = formatHeadCoachValue(nestedValue);
        return formatted ? `${formatHeadCoachFieldLabel(key)}: ${formatted}` : '';
      })
      .filter(Boolean)
      .join('; ');
  }
  return compactHeadCoachText(value);
};

const HeadCoachRecommendationItem = ({ item }) => {
  if (typeof item !== 'object' || item === null) {
    const text = compactHeadCoachText(item);
    return text ? (
      <li className="rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-700">
        {text}
      </li>
    ) : null;
  }

  const title = headCoachItemTitle(item);
  const details = Object.entries(item)
    .filter(([key, value]) => !['title', 'name', 'area', 'recommendation', 'action', 'text', 'summary', 'description'].includes(key) && value !== null && value !== undefined && value !== '')
    .map(([key, value]) => [key, formatHeadCoachValue(value)])
    .filter(([, value]) => value);

  if (!title && details.length === 0) return null;

  return (
    <li className="rounded-lg bg-zinc-50 p-3 text-sm text-zinc-700">
      {title ? <p className="font-black leading-6 text-zinc-950">{title}</p> : null}
      {details.length ? (
        <div className="mt-2 space-y-1">
          {details.map(([key, value]) => (
            <p key={key} className="leading-6">
              <span className="font-bold text-zinc-500">{formatHeadCoachFieldLabel(key)}:</span>{' '}
              <span className="font-semibold">{value}</span>
            </p>
          ))}
        </div>
      ) : null}
    </li>
  );
};

const HeadCoachRecommendationSection = ({ title, items }) => {
  if (!items.length) return null;
  return (
    <div>
      <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">{title}</p>
      <ul className="mt-2 space-y-2">
        {items.map((item, index) => (
          <HeadCoachRecommendationItem key={`${title}-${index}`} item={item} />
        ))}
      </ul>
    </div>
  );
};

const HeadCoachStrategyPanel = ({ strategy, loading, loadFailed, onRefresh, t }) => {
  const focusAreas = asHeadCoachArray(strategy?.priority_focus_areas);
  const trainingRecommendations = asHeadCoachArray(strategy?.training_recommendations);
  const recoveryRecommendations = asHeadCoachArray(strategy?.recovery_recommendations);
  const nutritionRecommendations = asHeadCoachArray(strategy?.nutrition_recommendations);
  const tournamentRecommendations = asHeadCoachArray(strategy?.tournament_recommendations);
  const missingData = asHeadCoachArray(strategy?.missing_data).map(formatHeadCoachValue).filter(Boolean);
  const dataQualityFlags = asHeadCoachArray(strategy?.data_quality_flags).map(formatHeadCoachValue).filter(Boolean);
  const hasActionablePlan =
    focusAreas.length ||
    trainingRecommendations.length ||
    recoveryRecommendations.length ||
    nutritionRecommendations.length ||
    tournamentRecommendations.length;

  return (
    <Panel className="p-5">
      <SectionHeader
        icon={<BrainCircuit size={20} strokeWidth={1.8} />}
        kicker={strategy?.confidence ? `${t('headCoach.confidence')}: ${strategy.confidence}` : null}
        title={t('headCoach.strategyTitle')}
        body={!loading && !strategy?.summary ? t('headCoach.notEnoughData') : null}
        action={(
          <SecondaryButton onClick={onRefresh} disabled={loading} className="min-h-10 px-3">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            <span className="sr-only sm:not-sr-only">{t('headCoach.refreshPlan')}</span>
          </SecondaryButton>
        )}
      />

      {loading ? (
        <div className="rounded-lg bg-zinc-50 p-4 text-sm font-bold text-zinc-600">
          <span className="inline-flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            {t('headCoach.loading')}
          </span>
        </div>
      ) : loadFailed ? (
        <div className="rounded-lg bg-rose-50 p-3 text-sm font-bold text-rose-700 ring-1 ring-rose-100">
          {t('headCoach.loadError')}
        </div>
      ) : (
        <div className="space-y-4">
          {strategy?.summary ? (
            <div>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
                {t('headCoach.strategySummary')}
              </p>
              <p className="mt-2 rounded-lg bg-lime-50 px-3 py-2 text-sm font-bold leading-6 text-lime-900 ring-1 ring-lime-100">
                {strategy.summary}
              </p>
            </div>
          ) : null}

          {!hasActionablePlan ? (
            <p className="rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-500">
              {t('headCoach.notEnoughData')}
            </p>
          ) : null}

          {focusAreas.length ? (
            <div>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
                {t('headCoach.priorityFocusAreas')}
              </p>
              <div className="mt-2 grid gap-2">
                {focusAreas.map((item, index) => {
                  const area = compactHeadCoachText(item?.area || item?.title || item?.name || headCoachItemTitle(item));
                  const reason = compactHeadCoachText(item?.reason || item?.description || item?.summary);
                  const priority = compactHeadCoachText(item?.priority);
                  const coaches = asHeadCoachArray(item?.recommended_coaches).map(formatHeadCoachValue).filter(Boolean);
                  return (
                    <div key={`${area}-${index}`} className="rounded-lg border border-zinc-200 bg-white p-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <p className="font-black text-zinc-950">{area || t('headCoach.priorityFocusAreas')}</p>
                        {priority ? (
                          <span className="rounded-full bg-zinc-100 px-2 py-1 text-xs font-black text-zinc-600">
                            {t('headCoach.priority')}: {priority}
                          </span>
                        ) : null}
                      </div>
                      {reason ? <p className="mt-2 text-sm font-semibold leading-6 text-zinc-600">{reason}</p> : null}
                      {coaches.length ? (
                        <p className="mt-2 text-xs font-bold leading-5 text-zinc-500">
                          {t('headCoach.recommendedCoaches')}: {coaches.join(', ')}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <HeadCoachRecommendationSection title={t('headCoach.trainingRecommendations')} items={trainingRecommendations} />
          <HeadCoachRecommendationSection title={t('headCoach.recoveryRecommendations')} items={recoveryRecommendations} />
          <HeadCoachRecommendationSection title={t('headCoach.nutritionRecommendations')} items={nutritionRecommendations} />
          <HeadCoachRecommendationSection title={t('headCoach.tournamentRecommendations')} items={tournamentRecommendations} />

          {(strategy?.confidence || strategy?.model_version) ? (
            <div className="grid gap-2 text-sm">
              {strategy?.confidence ? (
                <div className="flex items-center justify-between gap-3 rounded-lg bg-zinc-50 px-3 py-2">
                  <span className="font-bold text-zinc-500">{t('headCoach.confidence')}</span>
                  <span className="text-right font-black text-zinc-950">{strategy.confidence}</span>
                </div>
              ) : null}
              {strategy?.model_version ? (
                <div className="flex items-center justify-between gap-3 rounded-lg bg-zinc-50 px-3 py-2">
                  <span className="font-bold text-zinc-500">{t('headCoach.model')}</span>
                  <span className="text-right font-black text-zinc-950">{strategy.model_version}</span>
                </div>
              ) : null}
            </div>
          ) : null}

          {missingData.length ? (
            <div>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
                {t('headCoach.missingData')}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {missingData.map((item) => (
                  <span key={item} className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-bold text-zinc-600">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {dataQualityFlags.length ? (
            <div>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
                {t('headCoach.dataQuality')}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {dataQualityFlags.map((item) => (
                  <span key={item} className="rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-100">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  );
};

const asConnectedTrainingArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const connectedText = (value) => String(value ?? '').trim().replace(/\s+/g, ' ');

const connectedLabel = (value) => connectedText(value).replace(/_/g, ' ');

const connectedNumber = (value, digits = 1) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return Number.isInteger(number) ? String(number) : number.toFixed(digits).replace(/\.0$/, '');
};

const connectedDate = (value, language) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return connectedText(value);
  return new Intl.DateTimeFormat(language || undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const connectedDuration = (seconds, t) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return '';
  const minutes = Math.round(value / 60);
  return `${minutes} ${t('common.min', 'min')}`;
};

const connectedDistance = (meters) => {
  const value = Number(meters);
  if (!Number.isFinite(value) || value <= 0) return '';
  return value >= 1000 ? `${connectedNumber(value / 1000)} km` : `${connectedNumber(value, 0)} m`;
};

const mobileSyncStatusLabel = (status, t) => {
  const value = String(status?.status || '').trim().toLowerCase();
  const labels = {
    scheduled: t('connectedTraining.syncStatusScheduled'),
    syncing: t('connectedTraining.syncStatusSyncing'),
    success: t('connectedTraining.syncStatusSuccess'),
    no_new_data: t('connectedTraining.syncStatusNoNewData'),
    permission_required: t('connectedTraining.syncStatusPermissionRequired'),
    auth_required: t('connectedTraining.syncStatusAuthRequired'),
    config_missing: t('connectedTraining.syncStatusConfigMissing'),
    failed: t('connectedTraining.syncStatusFailed'),
    stale: t('connectedTraining.syncStatusStale'),
    disabled: t('connectedTraining.syncStatusDisabled'),
  };
  return labels[value] || t('connectedTraining.syncStatusDisabled');
};

const mobileSyncTone = (status) => {
  const value = String(status?.status || '').trim().toLowerCase();
  if (value === 'success' || value === 'no_new_data' || value === 'scheduled' || value === 'syncing') {
    return 'bg-lime-50 text-lime-700 ring-lime-100';
  }
  if (value === 'permission_required' || value === 'auth_required' || value === 'config_missing' || value === 'stale') {
    return 'bg-amber-50 text-amber-800 ring-amber-100';
  }
  if (value === 'failed') return 'bg-rose-50 text-rose-700 ring-rose-100';
  return 'bg-zinc-100 text-zinc-600 ring-zinc-200';
};

const SELF_LOGGED_EQUIPMENT_TYPES = ['treadmill', 'stationary_bike', 'elliptical', 'rowing_machine', 'smart_trainer', 'other'];

const localDateTimeDefaults = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return {
    date: local.toISOString().slice(0, 10),
    time: local.toISOString().slice(11, 16),
  };
};

const emptySelfLoggedWorkoutForm = () => ({
  equipment_type: 'treadmill',
  ...localDateTimeDefaults(),
  duration_min: '',
  distance_km: '',
  heart_rate_avg: '',
  perceived_exertion: '',
  calories: '',
  notes: '',
});

const emptyPhoneGuidedCompletionForm = () => ({
  duration_min: '',
  distance_km: '',
  heart_rate_avg: '',
  perceived_exertion: '',
  calories: '',
  notes: '',
});

const optionalNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const selfLoggedStartedAt = (form) => {
  const date = form.date || localDateTimeDefaults().date;
  const time = form.time || '00:00';
  return new Date(`${date}T${time}`).toISOString();
};

const selfLoggedMissingGuidance = (result, t) => {
  const flags = Array.isArray(result?.data_quality_flags) ? result.data_quality_flags : [];
  if (!flags.length) return null;
  const needsProfile = flags.some((flag) =>
    ['missing_body_weight_for_calorie_estimate', 'missing_age_for_calorie_estimate'].includes(flag)
  );
  const needsWorkoutData = flags.some((flag) =>
    ['missing_duration_for_calorie_estimate', 'missing_intensity_for_calorie_estimate'].includes(flag)
  );
  if (!needsProfile && !needsWorkoutData) return null;
  return {
    body: needsProfile
      ? t('connectedTraining.selfLoggedMissingProfileData')
      : t('connectedTraining.selfLoggedMissingWorkoutData'),
    needsProfile,
  };
};

const connectedImportSkippedCount = (result) => {
  const direct = optionalNumber(result?.skipped_duplicates_count);
  if (direct !== null) return direct;
  return [
    result?.skipped_metrics_count,
    result?.skipped_workouts_count,
    result?.skipped_sessions_count,
  ].reduce((total, value) => {
    const number = optionalNumber(value);
    return total + (number || 0);
  }, 0);
};

const connectedImportRejectedCount = (result) => {
  const direct = optionalNumber(result?.rejected_count);
  if (direct !== null) return direct;
  const errors = Array.isArray(result?.errors) ? result.errors.length : 0;
  return errors;
};

const connectedImportErrorText = (error) => {
  if (typeof error === 'string') return error;
  if (!error || typeof error !== 'object') return '';
  return [
    error.type,
    error.index !== undefined ? `#${error.index}` : null,
    error.reason,
    error.message,
    error.source_record_id,
  ].filter(Boolean).join(' · ');
};

const connectedImportStatusLabel = (status, t) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'completed') return t('connectedTraining.importStatusCompleted');
  if (normalized === 'completed_with_errors') return t('connectedTraining.importStatusCompletedWithErrors');
  if (normalized === 'failed') return t('connectedTraining.importStatusFailed');
  return connectedLabel(status);
};

const hasConnectedValue = (value) => value !== null && value !== undefined && value !== '';

const ConnectedMetaRow = ({ label, value }) => {
  if (!hasConnectedValue(value)) return null;
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-zinc-50 px-3 py-2 text-sm">
      <span className="font-bold text-zinc-500">{label}</span>
      <span className="text-right font-black text-zinc-950">{value}</span>
    </div>
  );
};

const ConnectedBadge = ({ children, tone = 'bg-zinc-100 text-zinc-600 ring-zinc-200' }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-black ring-1 ${tone}`}>
    {children}
  </span>
);

const ConnectedDetails = ({ title, body, badge, defaultOpen = false, className = '', children }) => (
  <details
    defaultOpen={defaultOpen}
    className={`group rounded-lg border border-zinc-200 bg-white p-4 ${className}`}
  >
    <summary className="flex cursor-pointer list-none items-start justify-between gap-3">
      <span>
        <span className="block text-sm font-black text-zinc-950">{title}</span>
        {body ? <span className="mt-1 block text-xs font-semibold leading-5 text-zinc-500">{body}</span> : null}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {badge}
        <ChevronDown className="mt-0.5 text-zinc-400 group-open:hidden" size={16} strokeWidth={1.8} />
        <ChevronUp className="mt-0.5 hidden text-zinc-400 group-open:block" size={16} strokeWidth={1.8} />
      </span>
    </summary>
    <div className="mt-4 border-t border-zinc-100 pt-4">
      {children}
    </div>
  </details>
);

const ConnectedSummaryTile = ({ label, value, tone = 'bg-zinc-50 text-zinc-700 ring-zinc-100' }) => (
  <div className={`rounded-lg px-3 py-2 ring-1 ${tone}`}>
    <p className="text-xs font-black uppercase tracking-[0.12em] opacity-75">{label}</p>
    <p className="mt-1 text-sm font-black leading-5">{value}</p>
  </div>
);

const ConnectedImportResultSummary = ({ result, t }) => {
  if (!result) return null;
  const errors = Array.isArray(result.errors) ? result.errors : [];
  return (
    <div className="mt-4 rounded-lg bg-zinc-50 p-3 ring-1 ring-zinc-100">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        <ConnectedMetaRow label={t('connectedTraining.importedMetrics')} value={connectedNumber(result.imported_metrics_count, 0)} />
        <ConnectedMetaRow label={t('connectedTraining.importedWorkouts')} value={connectedNumber(result.imported_workouts_count, 0)} />
        <ConnectedMetaRow label={t('connectedTraining.skippedDuplicates')} value={connectedNumber(connectedImportSkippedCount(result), 0)} />
        <ConnectedMetaRow label={t('connectedTraining.rejectedRecords')} value={connectedNumber(connectedImportRejectedCount(result), 0)} />
        <ConnectedMetaRow label={t('connectedTraining.energyExpendituresCreated')} value={connectedNumber(result.energy_expenditures_created, 0)} />
      </div>
      {errors.length ? (
        <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm font-bold leading-6 text-rose-700 ring-1 ring-rose-100">
          <p className="flex items-center gap-2 font-black">
            <AlertTriangle size={16} strokeWidth={1.8} />
            {t('connectedTraining.importErrors')}
          </p>
          <ul className="mt-2 space-y-1">
            {errors.slice(0, 6).map((error, index) => (
              <li key={`${connectedImportErrorText(error)}-${index}`}>
                {connectedImportErrorText(error) || t('connectedTraining.importError')}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const ConnectedTrainingPanel = ({ data, loading, loadFailed, onRefresh, language, t }) => {
  const devices = asConnectedTrainingArray(data?.devices);
  const metrics = asConnectedTrainingArray(data?.metrics);
  const workouts = asConnectedTrainingArray(data?.workouts);
  const prescriptions = asConnectedTrainingArray(data?.prescriptions);
  const imports = asConnectedTrainingArray(data?.imports);
  const mobileStatuses = asConnectedTrainingArray(data?.mobileStatuses);
  const equipmentCapabilities = asConnectedTrainingArray(data?.equipmentCapabilities);
  const equipmentDeliveries = asConnectedTrainingArray(data?.equipmentDeliveries);
  const phoneGuidedWorkouts = asConnectedTrainingArray(data?.phoneGuidedWorkouts);
  const syncedWorkoutMatches = asConnectedTrainingArray(data?.syncedWorkoutMatches);
  const syncedWorkoutSummary = summarizeSyncedWorkoutMatches(syncedWorkoutMatches);
  const syncedWorkoutCandidates = syncedWorkoutMatches.filter(isCandidateSyncedWorkoutMatch);
  const latestImport = imports[0] || null;
  const [showDeviceSyncDetails, setShowDeviceSyncDetails] = useState(false);
  const [showSyncedWorkoutCandidates, setShowSyncedWorkoutCandidates] = useState(false);
  const [selfLoggedForm, setSelfLoggedForm] = useState(emptySelfLoggedWorkoutForm);
  const [selfLoggedSaving, setSelfLoggedSaving] = useState(false);
  const [selfLoggedResult, setSelfLoggedResult] = useState(null);
  const [phoneGuidedStartingId, setPhoneGuidedStartingId] = useState(null);
  const [phoneGuidedCompletingId, setPhoneGuidedCompletingId] = useState(null);
  const [phoneGuidedCompletionForms, setPhoneGuidedCompletionForms] = useState({});
  const [syncedMatchActionId, setSyncedMatchActionId] = useState(null);
  const [importJson, setImportJson] = useState('');
  const [importSaving, setImportSaving] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [appleHealthFile, setAppleHealthFile] = useState(null);
  const [appleHealthDeviceId, setAppleHealthDeviceId] = useState('');
  const [appleHealthUploading, setAppleHealthUploading] = useState(false);
  const [appleHealthResult, setAppleHealthResult] = useState(null);
  const [appleHealthError, setAppleHealthError] = useState('');
  const missingGuidance = selfLoggedMissingGuidance(selfLoggedResult, t);
  const mobileImportedMetricsCount = mobileStatuses.reduce(
    (total, status) => total + (optionalNumber(status?.total_imported_metrics_count) || 0),
    0,
  );
  const mobileImportedWorkoutsCount = mobileStatuses.reduce(
    (total, status) => total + (optionalNumber(status?.total_imported_workouts_count) || 0),
    0,
  );
  const manualFallbackNeedsAttention = Boolean(selfLoggedResult || missingGuidance);
  const importFallbackNeedsAttention = Boolean(importResult || appleHealthResult || appleHealthError);
  const equipmentCapabilitySummary = summarizeEquipmentCapabilities(equipmentCapabilities);
  const equipmentDeliverySummary = summarizeEquipmentDeliveries(equipmentDeliveries);
  const hasRecentImportedData = mobileImportedMetricsCount > 0 || mobileImportedWorkoutsCount > 0 || imports.length > 0;
  const equipmentDeliveryStatusKey = {
    blocked_no_verified_capability: 'connectedTraining.equipmentDeliveryBlockedShort',
    ready_to_send: 'connectedTraining.equipmentDeliveryReadyShort',
    requires_user_confirmation: 'connectedTraining.equipmentDeliveryConfirmationShort',
  }[equipmentDeliverySummary.latestStatus] || 'connectedTraining.equipmentDeliveryNoRecentShort';
  const summaryTiles = [
    {
      label: t('connectedTraining.mobileSyncSummaryLabel'),
      value: mobileStatuses.length ? t('connectedTraining.mobileSyncActiveShort') : t('connectedTraining.mobileSyncNoReportShort'),
      tone: mobileStatuses.length ? 'bg-lime-50 text-lime-800 ring-lime-100' : 'bg-zinc-50 text-zinc-600 ring-zinc-100',
    },
    {
      label: t('connectedTraining.recentDataSummaryLabel'),
      value: hasRecentImportedData ? t('connectedTraining.recentImportedDataShort') : t('connectedTraining.noRecentSyncShort'),
      tone: hasRecentImportedData ? 'bg-lime-50 text-lime-800 ring-lime-100' : 'bg-zinc-50 text-zinc-600 ring-zinc-100',
    },
    {
      label: t('connectedTraining.equipmentCapabilitiesSummaryLabel'),
      value: t('connectedTraining.equipmentCapabilitiesSummary', {
        verified: equipmentCapabilitySummary.verifiedCount,
        unverified: equipmentCapabilitySummary.unverifiedOrDetectedCount,
      }),
      tone: equipmentCapabilitySummary.hasVerifiedWorkoutDelivery
        ? 'bg-lime-50 text-lime-800 ring-lime-100'
        : equipmentCapabilitySummary.hasUnverifiedCapability
          ? 'bg-amber-50 text-amber-800 ring-amber-100'
          : 'bg-zinc-50 text-zinc-600 ring-zinc-100',
    },
    {
      label: t('connectedTraining.deliverySummaryLabel'),
      value: t(equipmentDeliveryStatusKey),
      tone: equipmentDeliverySummary.latestStatusTone === 'ready'
        ? 'bg-lime-50 text-lime-800 ring-lime-100'
        : equipmentDeliverySummary.latestStatusTone === 'blocked'
          ? 'bg-rose-50 text-rose-700 ring-rose-100'
          : equipmentDeliverySummary.latestStatusTone === 'warning'
            ? 'bg-amber-50 text-amber-800 ring-amber-100'
            : 'bg-zinc-50 text-zinc-600 ring-zinc-100',
    },
    {
      label: t('connectedTraining.phoneGuidedSummaryLabel'),
      value: phoneGuidedWorkouts.length
        ? t('connectedTraining.equipmentFallbackAvailableShort', { count: phoneGuidedWorkouts.length })
        : t('connectedTraining.equipmentFallbackNoRecordsShort'),
      tone: phoneGuidedWorkouts.length ? 'bg-lime-50 text-lime-800 ring-lime-100' : 'bg-zinc-50 text-zinc-600 ring-zinc-100',
    },
    {
      label: t('connectedTraining.syncedWorkoutMatchesTitle'),
      value: syncedWorkoutSummary.hasMatches
        ? t('connectedTraining.syncedWorkoutMatchSummaryShort', {
          automatic: syncedWorkoutSummary.automaticCount,
          candidates: syncedWorkoutSummary.candidateCount,
        })
        : t('connectedTraining.syncedWorkoutNoMatchesShort'),
      tone: syncedWorkoutSummary.automaticCount
        ? 'bg-lime-50 text-lime-800 ring-lime-100'
        : syncedWorkoutSummary.candidateCount
          ? 'bg-amber-50 text-amber-800 ring-amber-100'
          : 'bg-zinc-50 text-zinc-600 ring-zinc-100',
    },
  ];

  const updateSelfLoggedForm = (key, value) => {
    setSelfLoggedForm((current) => ({ ...current, [key]: value }));
  };

  const submitSelfLoggedWorkout = async (event) => {
    event.preventDefault();
    const durationMin = optionalNumber(selfLoggedForm.duration_min);
    if (!durationMin || durationMin <= 0) {
      toast.error(t('connectedTraining.selfLoggedDurationRequired'));
      return;
    }
    const payload = {
      equipment_type: selfLoggedForm.equipment_type,
      started_at: selfLoggedStartedAt(selfLoggedForm),
      duration_sec: Math.round(durationMin * 60),
      distance_m: optionalNumber(selfLoggedForm.distance_km) !== null ? optionalNumber(selfLoggedForm.distance_km) * 1000 : null,
      heart_rate_avg: optionalNumber(selfLoggedForm.heart_rate_avg),
      perceived_exertion: optionalNumber(selfLoggedForm.perceived_exertion),
      calories: optionalNumber(selfLoggedForm.calories),
      notes: selfLoggedForm.notes || null,
      source: 'manual',
    };
    setSelfLoggedSaving(true);
    setSelfLoggedResult(null);
    try {
      const response = await connectedTraining.createSelfLoggedEquipmentWorkout(payload);
      setSelfLoggedResult(response.data || null);
      toast.success(t('connectedTraining.selfLoggedSaved'));
      setSelfLoggedForm(emptySelfLoggedWorkoutForm());
      await onRefresh();
    } catch (error) {
      toast.error(t('connectedTraining.selfLoggedSaveError'));
    } finally {
      setSelfLoggedSaving(false);
    }
  };

  const startPhoneGuidedWorkout = async (prescription, delivery) => {
    if (!prescription?.id) return;
    setPhoneGuidedStartingId(prescription.id);
    try {
      await connectedTraining.createPhoneGuidedEquipmentWorkout({
        workout_prescription_id: prescription.id,
        delivery_id: delivery?.id || null,
        guidance_mode: delivery?.delivery_mode === 'read_only' ? 'read_only' : 'phone_guided',
        delivery_block_reason:
          delivery?.status === 'blocked_no_verified_capability'
            ? 'blocked_no_verified_capability'
            : 'delivery_unavailable',
      });
      toast.success(t('connectedTraining.phoneGuidedStarted'));
      await onRefresh();
    } catch (error) {
      toast.error(t('connectedTraining.phoneGuidedStartError'));
    } finally {
      setPhoneGuidedStartingId(null);
    }
  };

  const updatePhoneGuidedCompletionForm = (sessionId, key, value) => {
    setPhoneGuidedCompletionForms((current) => ({
      ...current,
      [sessionId]: {
        ...(current[sessionId] || emptyPhoneGuidedCompletionForm()),
        [key]: value,
      },
    }));
  };

  const completePhoneGuidedWorkout = async (event, workout) => {
    event.preventDefault();
    if (!workout?.id) return;
    const form = phoneGuidedCompletionForms[workout.id] || emptyPhoneGuidedCompletionForm();
    const durationMin = optionalNumber(form.duration_min);
    if (!durationMin || durationMin <= 0) {
      toast.error(t('connectedTraining.selfLoggedDurationRequired'));
      return;
    }
    const payload = {
      completion_source: 'manual',
      duration_sec: Math.round(durationMin * 60),
      distance_m: optionalNumber(form.distance_km) !== null ? optionalNumber(form.distance_km) * 1000 : null,
      avg_heart_rate: optionalNumber(form.heart_rate_avg),
      perceived_exertion: optionalNumber(form.perceived_exertion),
      calories_burned: optionalNumber(form.calories),
      notes: form.notes || null,
    };
    setPhoneGuidedCompletingId(workout.id);
    try {
      await connectedTraining.completePhoneGuidedEquipmentWorkout(workout.id, payload);
      toast.success(t('connectedTraining.phoneGuidedCompleted'));
      setPhoneGuidedCompletionForms((current) => ({ ...current, [workout.id]: emptyPhoneGuidedCompletionForm() }));
      await onRefresh();
    } catch (error) {
      toast.error(t('connectedTraining.phoneGuidedCompleteError'));
    } finally {
      setPhoneGuidedCompletingId(null);
    }
  };

  const confirmSyncedWorkoutMatch = async (match) => {
    if (!match?.id) return;
    setSyncedMatchActionId(match.id);
    try {
      await connectedTraining.confirmSyncedWorkoutMatch(match.id);
      toast.success(t('connectedTraining.syncedWorkoutConfirmSuccess'));
      await onRefresh();
    } catch (error) {
      toast.error(t('connectedTraining.syncedWorkoutConfirmError'));
    } finally {
      setSyncedMatchActionId(null);
    }
  };

  const rejectSyncedWorkoutMatch = async (match) => {
    if (!match?.id) return;
    setSyncedMatchActionId(match.id);
    try {
      await connectedTraining.rejectSyncedWorkoutMatch(match.id);
      toast.success(t('connectedTraining.syncedWorkoutRejectSuccess'));
      await onRefresh();
    } catch (error) {
      toast.error(t('connectedTraining.syncedWorkoutRejectError'));
    } finally {
      setSyncedMatchActionId(null);
    }
  };

  const submitImportPayload = async (event) => {
    event.preventDefault();
    let payload;
    try {
      payload = JSON.parse(importJson);
    } catch (error) {
      toast.error(t('connectedTraining.importError'));
      return;
    }
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      toast.error(t('connectedTraining.importError'));
      return;
    }
    setImportSaving(true);
    try {
      const response = await connectedTraining.importData(payload);
      setImportResult(response.data || null);
      toast.success(t('connectedTraining.importSuccess'));
      await onRefresh();
    } catch (error) {
      toast.error(apiErrorMessage(error, t('connectedTraining.importError'), t));
    } finally {
      setImportSaving(false);
    }
  };

  const submitAppleHealthExport = async () => {
    if (!appleHealthFile) {
      const message = t('connectedTraining.appleHealthExportNoFile');
      setAppleHealthError(message);
      toast.error(message);
      return;
    }
    setAppleHealthUploading(true);
    setAppleHealthError('');
    try {
      const response = await connectedTraining.importAppleHealthExport(appleHealthFile, {
        device_id: appleHealthDeviceId.trim() || undefined,
      });
      setAppleHealthResult(response.data || null);
      toast.success(t('connectedTraining.appleHealthExportSuccess'));
      await onRefresh();
    } catch (error) {
      const message = apiErrorMessage(error, t('connectedTraining.appleHealthExportError'), t);
      setAppleHealthError(message);
      toast.error(message);
    } finally {
      setAppleHealthUploading(false);
    }
  };

  return (
    <Panel className="p-5">
      <SectionHeader
        icon={<Activity size={20} strokeWidth={1.8} />}
        kicker={t('connectedTraining.kicker', 'Devices and workouts')}
        title={t('connectedTraining.connectedTrainingSummaryTitle')}
        body={t('connectedTraining.connectedTrainingSummaryBody')}
        action={(
          <SecondaryButton onClick={onRefresh} disabled={loading} className="min-h-10 px-3">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            <span className="sr-only sm:not-sr-only">{t('connectedTraining.refresh')}</span>
          </SecondaryButton>
        )}
      />

      {loading ? (
        <div className="rounded-lg bg-zinc-50 p-4 text-sm font-bold text-zinc-600">
          <span className="inline-flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            {t('connectedTraining.loading')}
          </span>
        </div>
      ) : loadFailed ? (
        <div className="rounded-lg bg-rose-50 p-3 text-sm font-bold text-rose-700 ring-1 ring-rose-100">
          {t('connectedTraining.loadError')}
        </div>
      ) : (
        <div className="space-y-5">
          <div className="rounded-lg border border-zinc-200 bg-white p-4">
            <div className="grid gap-2 sm:grid-cols-2">
              {summaryTiles.map((item) => (
                <ConnectedSummaryTile key={item.label} label={item.label} value={item.value} tone={item.tone} />
              ))}
            </div>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <Link
                to="/profile"
                className="inline-flex min-h-10 items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50 px-3 text-sm font-black text-zinc-800 transition hover:bg-zinc-100"
              >
                {t('connectedTraining.manageDevicesInProfile')}
              </Link>
              <button
                type="button"
                onClick={() => setShowDeviceSyncDetails((current) => !current)}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-zinc-900 bg-zinc-900 px-3 text-sm font-black text-white transition hover:bg-zinc-800"
              >
                {showDeviceSyncDetails ? <ChevronUp size={16} strokeWidth={1.8} /> : <ChevronDown size={16} strokeWidth={1.8} />}
                {showDeviceSyncDetails ? t('connectedTraining.hideDevicesSyncDetails') : t('connectedTraining.showDevicesSyncDetails')}
              </button>
            </div>
          </div>

          {showDeviceSyncDetails ? (
          <div className="space-y-4">
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
                  {t('connectedTraining.automaticMobileSync')}
                </p>
                <h3 className="mt-1 text-lg font-black tracking-normal text-zinc-950">
                  {mobileStatuses.length ? t('connectedTraining.syncedFromPhone') : t('connectedTraining.automaticMobileSync')}
                </h3>
              </div>
              <ConnectedBadge tone="bg-white text-zinc-700 ring-zinc-200">
                {t('connectedTraining.syncStatusSource')}
              </ConnectedBadge>
            </div>
            <p className="mt-3 text-sm font-semibold leading-6 text-zinc-600">
              {t('connectedTraining.automaticMobileSyncDescription')}
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              <ConnectedMetaRow label={t('connectedTraining.syncSourcesCount')} value={connectedNumber(mobileStatuses.length, 0)} />
              <ConnectedMetaRow label={t('connectedTraining.importedMetrics')} value={connectedNumber(mobileImportedMetricsCount, 0)} />
              <ConnectedMetaRow label={t('connectedTraining.importedWorkouts')} value={connectedNumber(mobileImportedWorkoutsCount, 0)} />
            </div>
            {mobileStatuses.length ? (
              <ConnectedDetails
                title={t('connectedTraining.mobileSourceStatusDetails')}
                body={t('connectedTraining.mobileSourceStatusDetailsDescription')}
                badge={<ConnectedBadge>{connectedNumber(mobileStatuses.length, 0)}</ConnectedBadge>}
                className="mt-3 bg-white"
              >
                <div className="grid gap-3 md:grid-cols-2">
                  {mobileStatuses.map((status) => (
                    <div key={status.id || `${status.source}-${status.platform}-${status.device_id || status.app_instance_id || 'source'}`} className="rounded-lg border border-zinc-200 bg-white p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-black text-zinc-950">{connectedLabel(status.source) || t('connectedTraining.source')}</p>
                        <ConnectedBadge>{connectedLabel(status.platform)}</ConnectedBadge>
                        <ConnectedBadge tone={mobileSyncTone(status)}>{mobileSyncStatusLabel(status, t)}</ConnectedBadge>
                      </div>
                      <div className="mt-3 grid gap-2">
                        <ConnectedMetaRow
                          label={t('connectedTraining.backgroundSyncEnabled')}
                          value={status.background_sync_enabled ? t('common.yes') : t('common.no')}
                        />
                        <ConnectedMetaRow
                          label={t('connectedTraining.lastSuccessfulSync')}
                          value={connectedDate(status.last_successful_sync_at, language)}
                        />
                        <ConnectedMetaRow
                          label={t('connectedTraining.lastProviderTimestamp')}
                          value={connectedDate(status.last_provider_timestamp, language)}
                        />
                        <ConnectedMetaRow
                          label={t('connectedTraining.importedMetrics')}
                          value={connectedNumber(status.total_imported_metrics_count, 0)}
                        />
                        <ConnectedMetaRow
                          label={t('connectedTraining.importedWorkouts')}
                          value={connectedNumber(status.total_imported_workouts_count, 0)}
                        />
                        <ConnectedMetaRow
                          label={t('connectedTraining.skippedRejected')}
                          value={`${connectedNumber(status.total_skipped_count, 0) || 0} / ${connectedNumber(status.total_rejected_count, 0) || 0}`}
                        />
                      </div>
                      {status.last_error_code || status.last_error_message ? (
                        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-bold leading-5 text-amber-800 ring-1 ring-amber-100">
                          {t('connectedTraining.mobileSourceLastError')}: {status.last_error_message || status.last_error_code}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </ConnectedDetails>
            ) : (
              <p className="mt-3 rounded-lg bg-white px-3 py-2 text-sm font-bold leading-6 text-zinc-500 ring-1 ring-zinc-100">
                {t('connectedTraining.noMobileSyncStatus')}
              </p>
            )}
          </div>

          <ConnectedDetails
            title={t('connectedTraining.manualCardioEquipmentFallback')}
            body={t('connectedTraining.manualCardioEquipmentFallbackDescription')}
            badge={<ConnectedBadge tone="bg-amber-50 text-amber-800 ring-amber-100">{t('connectedTraining.fallbackOnly')}</ConnectedBadge>}
            defaultOpen={manualFallbackNeedsAttention}
          >
          <form onSubmit={submitSelfLoggedWorkout} className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
                  {t('connectedTraining.selfLoggedKicker')}
                </p>
                <h3 className="mt-1 text-lg font-black tracking-normal text-zinc-950">
                  {t('connectedTraining.addSelfLoggedWorkout')}
                </h3>
              </div>
              <ConnectedBadge tone="bg-amber-50 text-amber-800 ring-amber-100">
                {t('connectedTraining.manualOnly')}
              </ConnectedBadge>
            </div>
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-bold leading-5 text-amber-800 ring-1 ring-amber-100">
              {t('connectedTraining.selfLoggedSafetyText')}
            </p>
            <p className="mt-3 text-sm font-semibold leading-6 text-zinc-600">
              {t('connectedTraining.selfLoggedCaloriesHelp')}
            </p>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-zinc-700">{t('connectedTraining.equipmentType')}</span>
                <div className="relative">
                  <select
                    className="min-h-11 w-full appearance-none rounded-lg border border-zinc-200 bg-white px-3 pr-9 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
                    value={selfLoggedForm.equipment_type}
                    onChange={(event) => updateSelfLoggedForm('equipment_type', event.target.value)}
                  >
                    {SELF_LOGGED_EQUIPMENT_TYPES.map((type) => (
                      <option key={type} value={type}>{connectedLabel(type)}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16} strokeWidth={1.8} />
                </div>
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-zinc-700">{t('common.date')}</span>
                <input
                  type="date"
                  className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
                  value={selfLoggedForm.date}
                  onChange={(event) => updateSelfLoggedForm('date', event.target.value)}
                  required
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-zinc-700">{t('coach.time')}</span>
                <input
                  type="time"
                  className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
                  value={selfLoggedForm.time}
                  onChange={(event) => updateSelfLoggedForm('time', event.target.value)}
                  required
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-zinc-700">{t('connectedTraining.durationMinutes')}</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
                  value={selfLoggedForm.duration_min}
                  onChange={(event) => updateSelfLoggedForm('duration_min', event.target.value)}
                  required
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-zinc-700">{t('connectedTraining.distanceKm')}</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
                  value={selfLoggedForm.distance_km}
                  onChange={(event) => updateSelfLoggedForm('distance_km', event.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-zinc-700">{t('connectedTraining.heartRate')}</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
                  value={selfLoggedForm.heart_rate_avg}
                  onChange={(event) => updateSelfLoggedForm('heart_rate_avg', event.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-zinc-700">{t('connectedTraining.rpe')}</span>
                <input
                  type="number"
                  min="1"
                  max="10"
                  step="1"
                  className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
                  value={selfLoggedForm.perceived_exertion}
                  onChange={(event) => updateSelfLoggedForm('perceived_exertion', event.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-zinc-700">{t('connectedTraining.manualCalories')}</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
                  value={selfLoggedForm.calories}
                  onChange={(event) => updateSelfLoggedForm('calories', event.target.value)}
                />
              </label>
              <label className="block md:col-span-2">
                <span className="mb-2 block text-sm font-bold text-zinc-700">{t('common.notes')}</span>
                <textarea
                  className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
                  rows={2}
                  value={selfLoggedForm.notes}
                  onChange={(event) => updateSelfLoggedForm('notes', event.target.value)}
                />
              </label>
            </div>

            <button
              type="submit"
              disabled={selfLoggedSaving}
              className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {selfLoggedSaving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} strokeWidth={1.8} />}
              {t('connectedTraining.saveSelfLoggedWorkout')}
            </button>

            {selfLoggedResult?.metadata?.energy_expenditure_explanation ? (
              <p className="mt-3 rounded-lg bg-lime-50 px-3 py-2 text-sm font-bold leading-6 text-lime-800 ring-1 ring-lime-100">
                {selfLoggedResult.metadata.energy_expenditure_explanation}
              </p>
            ) : null}
            {missingGuidance ? (
              <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm font-bold leading-6 text-amber-800 ring-1 ring-amber-100">
                <p>{missingGuidance.body}</p>
                {missingGuidance.needsProfile ? (
                  <a className="mt-2 inline-flex text-amber-900 underline" href="/profile">
                    {t('connectedTraining.updateProfileForCalories')}
                  </a>
                ) : null}
              </div>
            ) : null}
          </form>
          </ConnectedDetails>

          <ConnectedDetails
            title={t('connectedTraining.advancedFallbackTools')}
            body={t('connectedTraining.advancedFallbackToolsDescription')}
            badge={<ConnectedBadge tone="bg-amber-50 text-amber-800 ring-amber-100">{t('connectedTraining.fallbackOnly')}</ConnectedBadge>}
            defaultOpen={importFallbackNeedsAttention}
            className="bg-zinc-50"
          >
          <form onSubmit={submitImportPayload} className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
                  {t('connectedTraining.advancedImportTools')}
                </p>
                <h3 className="mt-1 text-lg font-black tracking-normal text-zinc-950">
                  {t('connectedTraining.manualImportFallback')}
                </h3>
              </div>
              <ConnectedBadge tone="bg-amber-50 text-amber-800 ring-amber-100">
                {t('connectedTraining.fallbackOnly')}
              </ConnectedBadge>
            </div>
            <p className="mt-3 text-sm font-semibold leading-6 text-zinc-600">
              {t('connectedTraining.manualImportFallbackDescription')}
            </p>
            <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-blue-700">
                    {t('connectedTraining.manualAppleHealthImportOnly')}
                  </p>
                  <h4 className="mt-1 text-base font-black tracking-normal text-zinc-950">
                    {t('connectedTraining.appleHealthExportTitle')}
                  </h4>
                </div>
                <ConnectedBadge tone="bg-white text-blue-700 ring-blue-100">
                  {t('connectedTraining.fallbackOnly')}
                </ConnectedBadge>
              </div>
              <p className="mt-3 text-sm font-semibold leading-6 text-zinc-700">
                {t('connectedTraining.appleHealthExportDescription')}
              </p>
              <p className="mt-3 rounded-lg bg-white px-3 py-2 text-xs font-bold leading-5 text-blue-800 ring-1 ring-blue-100">
                {t('connectedTraining.appleHealthExportPrivacyNote')}
              </p>
              <div className="mt-4 grid gap-3 md:grid-cols-[1fr_0.75fr]">
                <label className="block">
                  <span className="mb-2 block text-sm font-bold text-zinc-700">
                    {t('connectedTraining.chooseAppleHealthExportFile')}
                  </span>
                  <input
                    type="file"
                    accept=".xml,text/xml,application/xml"
                    className="block min-h-11 w-full rounded-lg border border-blue-100 bg-white px-3 py-2 text-sm font-bold text-zinc-700 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-100 file:px-3 file:py-2 file:text-sm file:font-black file:text-blue-800 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    onChange={(event) => {
                      setAppleHealthFile(event.target.files?.[0] || null);
                      setAppleHealthError('');
                    }}
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-bold text-zinc-700">
                    {t('connectedTraining.device')}
                  </span>
                  <input
                    type="text"
                    className="min-h-11 w-full rounded-lg border border-blue-100 bg-white px-3 text-zinc-950 outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                    value={appleHealthDeviceId}
                    onChange={(event) => setAppleHealthDeviceId(event.target.value)}
                    placeholder="iphone-export"
                  />
                </label>
              </div>
              <button
                type="button"
                disabled={appleHealthUploading}
                onClick={submitAppleHealthExport}
                className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-blue-700 bg-blue-700 px-4 text-sm font-black text-white transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {appleHealthUploading ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} strokeWidth={1.8} />}
                {appleHealthUploading ? t('connectedTraining.uploadingAppleHealthExport') : t('connectedTraining.uploadAppleHealthExport')}
              </button>
              {appleHealthError ? (
                <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm font-bold leading-6 text-rose-700 ring-1 ring-rose-100">
                  {appleHealthError}
                </p>
              ) : null}
              {appleHealthResult ? (
                <div>
                  <p className="mt-4 text-sm font-black text-blue-800">
                    {t('connectedTraining.appleHealthExportSuccess')}
                  </p>
                  <ConnectedImportResultSummary result={appleHealthResult} t={t} />
                </div>
              ) : null}
            </div>
            <label className="mt-4 block">
              <span className="mb-2 block text-sm font-bold text-zinc-700">{t('connectedTraining.importData')}</span>
              <textarea
                className="min-h-40 w-full resize-y rounded-lg border border-zinc-200 bg-white px-3 py-2 font-mono text-sm text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
                value={importJson}
                onChange={(event) => setImportJson(event.target.value)}
                placeholder={t('connectedTraining.importJsonPlaceholder')}
                spellCheck={false}
              />
            </label>
            <button
              type="submit"
              disabled={importSaving || !importJson.trim()}
              className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-900 bg-zinc-900 px-4 text-sm font-black text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {importSaving ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} strokeWidth={1.8} />}
              {t('connectedTraining.importData')}
            </button>

            {importResult ? (
              <div className="mt-4 rounded-lg bg-zinc-50 p-3 ring-1 ring-zinc-100">
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  <ConnectedMetaRow label={t('connectedTraining.importedMetrics')} value={connectedNumber(importResult.imported_metrics_count, 0)} />
                  <ConnectedMetaRow label={t('connectedTraining.importedWorkouts')} value={connectedNumber(importResult.imported_workouts_count, 0)} />
                  <ConnectedMetaRow label={t('connectedTraining.importedSessions')} value={connectedNumber(importResult.imported_sessions_count, 0)} />
                  <ConnectedMetaRow label={t('connectedTraining.skippedDuplicates')} value={connectedNumber(connectedImportSkippedCount(importResult), 0)} />
                  <ConnectedMetaRow label={t('connectedTraining.rejectedRecords')} value={connectedNumber(connectedImportRejectedCount(importResult), 0)} />
                </div>
                {Array.isArray(importResult.errors) && importResult.errors.length ? (
                  <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm font-bold leading-6 text-rose-700 ring-1 ring-rose-100">
                    <p className="flex items-center gap-2 font-black">
                      <AlertTriangle size={16} strokeWidth={1.8} />
                      {t('connectedTraining.importErrors')}
                    </p>
                    <ul className="mt-2 space-y-1">
                      {importResult.errors.slice(0, 6).map((error, index) => (
                        <li key={`${connectedImportErrorText(error)}-${index}`}>
                          {connectedImportErrorText(error) || t('connectedTraining.importError')}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="mt-3 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-500">
                {t('connectedTraining.noImportResult')}
              </p>
            )}

            <div className="mt-5 border-t border-zinc-100 pt-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
                  {t('connectedTraining.importHistoryTitle')}
                </p>
                {latestImport ? (
                  <ConnectedBadge tone="bg-lime-50 text-lime-700 ring-lime-100">
                    {t('connectedTraining.latestImport')}: {connectedImportStatusLabel(latestImport.status, t)}
                  </ConnectedBadge>
                ) : null}
              </div>
              <p className="mt-2 text-sm font-semibold leading-6 text-zinc-600">
                {t('connectedTraining.importHistoryDescription')}
              </p>
              {imports.length ? (
                <div className="mt-3 space-y-2">
                  {imports.slice(0, 5).map((item, index) => (
                    <div key={item?.id || `${item?.source}-${item?.started_at}-${index}`} className="rounded-lg border border-zinc-200 bg-white p-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="font-black text-zinc-950">
                            {connectedLabel(item?.source) || t('connectedTraining.importSource')}
                          </p>
                          <p className="mt-1 text-xs font-semibold text-zinc-500">
                            {[connectedDate(item?.started_at, language), connectedDate(item?.finished_at, language)].filter(Boolean).join(' - ')}
                          </p>
                        </div>
                        <ConnectedBadge tone={item?.status === 'failed' ? 'bg-rose-50 text-rose-700 ring-rose-100' : item?.status === 'completed_with_errors' ? 'bg-amber-50 text-amber-800 ring-amber-100' : 'bg-lime-50 text-lime-700 ring-lime-100'}>
                          {connectedImportStatusLabel(item?.status, t)}
                        </ConnectedBadge>
                      </div>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        <ConnectedMetaRow label={t('connectedTraining.importSource')} value={connectedLabel(item?.source)} />
                        <ConnectedMetaRow label={t('connectedTraining.importStartedAt')} value={connectedDate(item?.started_at, language)} />
                        <ConnectedMetaRow label={t('connectedTraining.importFinishedAt')} value={connectedDate(item?.finished_at, language)} />
                        <ConnectedMetaRow label={t('connectedTraining.importedMetrics')} value={connectedNumber(item?.imported_metrics_count, 0)} />
                        <ConnectedMetaRow label={t('connectedTraining.skippedMetrics')} value={connectedNumber(item?.skipped_metrics_count, 0)} />
                        <ConnectedMetaRow label={t('connectedTraining.importedWorkouts')} value={connectedNumber(item?.imported_workouts_count, 0)} />
                        <ConnectedMetaRow label={t('connectedTraining.skippedWorkouts')} value={connectedNumber(item?.skipped_workouts_count, 0)} />
                        <ConnectedMetaRow label={t('connectedTraining.importedSessions')} value={connectedNumber(item?.imported_sessions_count, 0)} />
                        <ConnectedMetaRow label={t('connectedTraining.skippedSessions')} value={connectedNumber(item?.skipped_sessions_count, 0)} />
                        <ConnectedMetaRow label={t('connectedTraining.energyExpendituresCreated')} value={connectedNumber(item?.energy_expenditures_created, 0)} />
                        <ConnectedMetaRow label={t('connectedTraining.rejectedRecords')} value={connectedNumber(item?.rejected_count, 0)} />
                      </div>
                      {Array.isArray(item?.errors) && item.errors.length ? (
                        <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs font-bold leading-5 text-rose-700 ring-1 ring-rose-100">
                          <p className="font-black">{t('connectedTraining.importRunErrors')}</p>
                          <ul className="mt-1 space-y-1">
                            {item.errors.slice(0, 4).map((error, errorIndex) => (
                              <li key={`${connectedImportErrorText(error)}-${errorIndex}`}>
                                {connectedImportErrorText(error) || t('connectedTraining.importError')}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-3 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-500">
                  {t('connectedTraining.noImportHistory')}
                </p>
              )}
            </div>
          </form>
          </ConnectedDetails>

          <ConnectedDetails
            title={t('connectedTraining.connectedDeviceDetails')}
            body={t('connectedTraining.connectedDeviceDetailsDescription')}
            badge={<ConnectedBadge>{connectedNumber(devices.length, 0)}</ConnectedBadge>}
          >
            <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
              {t('connectedTraining.devices')}
            </p>
            {devices.length ? (
              <div className="mt-2 space-y-2">
                {devices.slice(0, 4).map((device, index) => {
                  const capabilities = device?.capabilities || {};
                  const capabilityBadges = [
                    capabilities.metrics_read ? t('connectedTraining.capabilityReadsMetrics') : null,
                    capabilities.workout_import ? t('connectedTraining.capabilityImportsWorkouts') : null,
                    capabilities.live_metrics ? t('connectedTraining.capabilityLiveMetrics') : null,
                    capabilities.supports_control ? t('connectedTraining.capabilitySupportsControl') : null,
                    capabilities.control_requires_confirmation ? t('connectedTraining.controlRequiresConfirmation') : null,
                  ].filter(Boolean);
                  return (
                    <div key={device?.id || `${device?.display_name}-${index}`} className="rounded-lg border border-zinc-200 bg-white p-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="font-black text-zinc-950">{connectedText(device?.display_name) || t('connectedTraining.device')}</p>
                          <p className="mt-1 text-xs font-bold text-zinc-500">
                            {[connectedLabel(device?.device_type), connectedLabel(device?.provider)].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                        {device?.connection_status ? (
                          <ConnectedBadge>{connectedLabel(device.connection_status)}</ConnectedBadge>
                        ) : null}
                      </div>
                      {device?.last_sync_at ? (
                        <p className="mt-2 text-xs font-semibold text-zinc-500">
                          {t('connectedTraining.lastSync')}: {connectedDate(device.last_sync_at, language)}
                        </p>
                      ) : null}
                      {capabilityBadges.length ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {capabilityBadges.map((badge) => (
                            <ConnectedBadge key={badge} tone="bg-lime-50 text-lime-700 ring-lime-100">{badge}</ConnectedBadge>
                          ))}
                        </div>
                      ) : null}
                      {capabilities.supports_control ? (
                        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-bold leading-5 text-amber-800 ring-1 ring-amber-100">
                          {t('connectedTraining.controlNotEnabled')}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="mt-2 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-500">
                {t('connectedTraining.noDevices')}
              </p>
            )}
          </ConnectedDetails>

          <ConnectedDetails
            title={t('connectedTraining.rawDeviceMetrics')}
            body={t('connectedTraining.rawDeviceMetricsDescription')}
            badge={<ConnectedBadge>{connectedNumber(metrics.length, 0)}</ConnectedBadge>}
          >
            <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
              {t('connectedTraining.metrics')}
            </p>
            {metrics.length ? (
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {metrics.slice(0, 8).map((metric, index) => (
                  <div key={metric?.id || `${metric?.metric_type}-${index}`} className="rounded-lg bg-zinc-50 p-3">
                    <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
                      {connectedLabel(metric?.metric_type) || t('connectedTraining.metric')}
                    </p>
                    <p className="mt-1 text-lg font-black text-zinc-950">
                      {[connectedNumber(metric?.value), connectedText(metric?.unit)].filter(Boolean).join(' ')}
                    </p>
                    <p className="mt-1 text-xs font-semibold text-zinc-500">
                      {[connectedLabel(metric?.source), connectedDate(metric?.recorded_at, language)].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-500">
                {t('connectedTraining.noMetrics')}
              </p>
            )}
          </ConnectedDetails>

          <ConnectedDetails
            title={t('connectedTraining.recentEquipmentFallbackHistory')}
            body={t('connectedTraining.recentEquipmentFallbackHistoryDescription')}
            badge={<ConnectedBadge>{connectedNumber(workouts.length, 0)}</ConnectedBadge>}
          >
            <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
              {t('connectedTraining.equipmentWorkouts')}
            </p>
            {workouts.length ? (
              <div className="mt-2 space-y-2">
                {workouts.slice(0, 4).map((workout, index) => (
                  <div key={workout?.id || `${workout?.equipment_type}-${index}`} className="rounded-lg border border-zinc-200 bg-white p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="font-black text-zinc-950">{connectedLabel(workout?.equipment_type) || t('connectedTraining.equipmentWorkout')}</p>
                      <ConnectedBadge>{connectedLabel(workout?.source) || t('connectedTraining.source')}</ConnectedBadge>
                    </div>
                    <p className="mt-1 text-xs font-semibold text-zinc-500">
                      {[connectedDate(workout?.started_at, language), connectedDate(workout?.ended_at, language)].filter(Boolean).join(' - ')}
                    </p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      <ConnectedMetaRow label={t('connectedTraining.duration')} value={connectedDuration(workout?.duration_sec, t)} />
                      <ConnectedMetaRow label={t('connectedTraining.distance')} value={connectedDistance(workout?.distance_m)} />
                      <ConnectedMetaRow label={t('connectedTraining.speed')} value={connectedNumber(workout?.avg_speed)} />
                      <ConnectedMetaRow label={t('connectedTraining.cadence')} value={connectedNumber(workout?.avg_cadence)} />
                      <ConnectedMetaRow label={t('connectedTraining.power')} value={connectedNumber(workout?.avg_power)} />
                      <ConnectedMetaRow label={t('connectedTraining.resistance')} value={connectedNumber(workout?.avg_resistance)} />
                      <ConnectedMetaRow label={t('connectedTraining.incline')} value={connectedNumber(workout?.incline_avg)} />
                      <ConnectedMetaRow label={t('connectedTraining.calories')} value={connectedNumber(workout?.calories, 0)} />
                      <ConnectedMetaRow label={t('connectedTraining.heartRate')} value={connectedNumber(workout?.heart_rate_avg, 0)} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-500">
                {t('connectedTraining.noEquipmentWorkouts')}
              </p>
            )}
          </ConnectedDetails>

          </div>
          ) : null}

          <div className="rounded-lg border border-zinc-200 bg-white p-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
                  {t('connectedTraining.syncedWorkoutMatchesTitle')}
                </p>
                <p className="mt-1 text-sm font-bold leading-6 text-zinc-700">
                  {syncedWorkoutSummary.hasMatches
                    ? t('connectedTraining.syncedWorkoutMatchesBody')
                    : t('connectedTraining.syncedWorkoutNoMatches')}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <ConnectedBadge tone="bg-lime-50 text-lime-800 ring-lime-100">
                  {t('connectedTraining.syncedWorkoutAutoCompletedCount', { count: syncedWorkoutSummary.automaticCount })}
                </ConnectedBadge>
                <ConnectedBadge tone={syncedWorkoutSummary.candidateCount ? 'bg-amber-50 text-amber-800 ring-amber-100' : 'bg-zinc-100 text-zinc-600 ring-zinc-200'}>
                  {t('connectedTraining.syncedWorkoutCandidateCount', { count: syncedWorkoutSummary.candidateCount })}
                </ConnectedBadge>
              </div>
            </div>
            <p className="mt-2 rounded-lg bg-zinc-50 px-3 py-2 text-xs font-bold leading-5 text-zinc-600 ring-1 ring-zinc-100">
              {t('connectedTraining.syncedWorkoutManualFallbackNote')}
            </p>
            {syncedWorkoutSummary.latest ? (
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <ConnectedMetaRow
                  label={t('connectedTraining.latestMatchSource')}
                  value={t(syncedWorkoutSourceKey(syncedWorkoutSummary.latest.source))}
                />
                <ConnectedMetaRow
                  label={t('connectedTraining.confidence')}
                  value={t(syncedWorkoutConfidenceKey(syncedWorkoutSummary.latest.confidence))}
                />
                <ConnectedMetaRow
                  label={t('connectedTraining.completionSource')}
                  value={t(syncedWorkoutCompletionSourceKey(syncedWorkoutSummary.latest.completion_source))}
                />
                <ConnectedMetaRow
                  label={t('connectedTraining.status')}
                  value={t(syncedWorkoutMatchStatusKey(syncedWorkoutSummary.latest))}
                />
              </div>
            ) : null}
            {syncedWorkoutCandidates.length ? (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setShowSyncedWorkoutCandidates((current) => !current)}
                  className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 text-xs font-black text-amber-900 transition hover:bg-amber-100"
                >
                  {showSyncedWorkoutCandidates ? <ChevronUp size={14} strokeWidth={1.8} /> : <ChevronDown size={14} strokeWidth={1.8} />}
                  {t('connectedTraining.syncedWorkoutReviewCandidate')}
                </button>
                {showSyncedWorkoutCandidates ? (
                  <div className="mt-3 space-y-2">
                    {syncedWorkoutCandidates.slice(0, 5).map((match) => {
                      const stats = match?.workout_stats || {};
                      const reasons = Array.isArray(match?.reasons) ? match.reasons : [];
                      return (
                        <div key={match.id} className="rounded-lg bg-amber-50 p-3 ring-1 ring-amber-100">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-black text-amber-950">
                                {t('connectedTraining.syncedWorkoutCandidateFound')}
                              </p>
                              <p className="mt-1 text-xs font-bold leading-5 text-amber-800">
                                {[connectedDate(stats.started_at, language), connectedDate(stats.ended_at, language)].filter(Boolean).join(' - ')}
                              </p>
                            </div>
                            <ConnectedBadge tone="bg-white text-amber-800 ring-amber-200">
                              {t(syncedWorkoutConfidenceKey(match.confidence))}
                            </ConnectedBadge>
                          </div>
                          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                            <ConnectedMetaRow label={t('connectedTraining.source')} value={t(syncedWorkoutSourceKey(match.source))} />
                            <ConnectedMetaRow label={t('connectedTraining.duration')} value={connectedDuration(stats.duration_sec, t)} />
                            <ConnectedMetaRow label={t('connectedTraining.distance')} value={connectedDistance(stats.distance_m)} />
                            <ConnectedMetaRow label={t('connectedTraining.calories')} value={connectedNumber(stats.calories, 0)} />
                          </div>
                          {reasons.length ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {reasons.slice(0, 4).map((reason) => (
                                <span key={reason} className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-amber-800 ring-1 ring-amber-100">
                                  {connectedLabel(reason)}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => confirmSyncedWorkoutMatch(match)}
                              disabled={syncedMatchActionId === match.id}
                              className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-3 text-xs font-black text-zinc-950 transition hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {syncedMatchActionId === match.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} strokeWidth={1.8} />}
                              {t('connectedTraining.syncedWorkoutConfirmMatch')}
                            </button>
                            <button
                              type="button"
                              onClick={() => rejectSyncedWorkoutMatch(match)}
                              disabled={syncedMatchActionId === match.id}
                              className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 text-xs font-black text-zinc-700 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {t('connectedTraining.syncedWorkoutRejectMatch')}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
              {t('connectedTraining.prescriptions')}
            </p>
            {prescriptions.length ? (
              <div className="mt-2 space-y-3">
                {prescriptions.slice(0, 4).map((prescription, index) => {
                  const segments = asConnectedTrainingArray(prescription?.segments);
                  const delivery = findBlockedEquipmentDelivery(prescription, equipmentDeliveries);
                  const phoneGuidedWorkout = findPhoneGuidedWorkout(prescription, phoneGuidedWorkouts, delivery);
                  const syncedMatch = findSyncedMatchForPrescription(prescription, syncedWorkoutMatches);
                  const hasAutomaticSyncedMatch = isAutomaticSyncedWorkoutMatch(syncedMatch);
                  const completionForm = phoneGuidedCompletionForms[phoneGuidedWorkout?.id] || emptyPhoneGuidedCompletionForm();
                  const showPhoneGuidedFallback = isEquipmentPrescription(prescription) && (delivery || phoneGuidedWorkout);
                  return (
                    <div key={prescription?.id || `${prescription?.title}-${index}`} className="rounded-lg border border-zinc-200 bg-white p-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="font-black text-zinc-950">{connectedText(prescription?.title) || t('connectedTraining.prescription')}</p>
                          {prescription?.purpose ? (
                            <p className="mt-1 text-sm font-semibold leading-6 text-zinc-600">{prescription.purpose}</p>
                          ) : null}
                        </div>
                        {prescription?.status ? <ConnectedBadge>{connectedLabel(prescription.status)}</ConnectedBadge> : null}
                      </div>
                      <div className="mt-3 grid gap-2">
                        <ConnectedMetaRow label={t('connectedTraining.targetDevice')} value={connectedLabel(prescription?.target_device_type)} />
                        <ConnectedMetaRow label={t('connectedTraining.workoutType')} value={connectedLabel(prescription?.workout_type)} />
                        <ConnectedMetaRow label={t('connectedTraining.scheduledFor')} value={connectedDate(prescription?.scheduled_for, language)} />
                      </div>
                      {prescription?.safety_notes ? (
                        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-bold leading-5 text-amber-800 ring-1 ring-amber-100">
                          {t('connectedTraining.safetyNotes')}: {prescription.safety_notes}
                        </p>
                      ) : null}
                      {syncedMatch ? (
                        <div className={`mt-3 rounded-lg p-3 ring-1 ${hasAutomaticSyncedMatch ? 'bg-lime-50 text-lime-900 ring-lime-100' : 'bg-amber-50 text-amber-900 ring-amber-100'}`}>
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-black">
                                {t(syncedWorkoutMatchStatusKey(syncedMatch))}
                              </p>
                              <p className="mt-1 text-xs font-bold leading-5">
                                {hasAutomaticSyncedMatch
                                  ? t('connectedTraining.syncedWorkoutAutoCompletionBody')
                                  : t('connectedTraining.syncedWorkoutCandidateReviewBody')}
                              </p>
                            </div>
                            <ConnectedBadge tone={hasAutomaticSyncedMatch ? 'bg-white text-lime-800 ring-lime-200' : 'bg-white text-amber-800 ring-amber-200'}>
                              {t(syncedWorkoutConfidenceKey(syncedMatch.confidence))}
                            </ConnectedBadge>
                          </div>
                          <div className="mt-3 grid gap-2 sm:grid-cols-3">
                            <ConnectedMetaRow label={t('connectedTraining.source')} value={t(syncedWorkoutSourceKey(syncedMatch.source))} />
                            <ConnectedMetaRow label={t('connectedTraining.completionSource')} value={t(syncedWorkoutCompletionSourceKey(syncedMatch.completion_source))} />
                            <ConnectedMetaRow label={t('connectedTraining.calories')} value={connectedNumber(syncedMatch?.workout_stats?.calories, 0)} />
                          </div>
                        </div>
                      ) : null}
                      {segments.length ? (
                        <div className="mt-3">
                          <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
                            {t('connectedTraining.segments')}
                          </p>
                          <div className="mt-2 space-y-2">
                            {segments.map((segment, segmentIndex) => (
                              <div key={`${segment?.order || segmentIndex}-${segment?.name}`} className="rounded-lg bg-zinc-50 p-3 text-sm">
                                <p className="font-black text-zinc-950">
                                  {segment?.order ? `${segment.order}. ` : ''}{connectedText(segment?.name) || t('connectedTraining.segment')}
                                </p>
                                <div className="mt-2 grid gap-2">
                                  <ConnectedMetaRow label={t('connectedTraining.duration')} value={connectedDuration(segment?.duration_sec, t)} />
                                  <ConnectedMetaRow label={t('connectedTraining.distance')} value={connectedDistance(segment?.distance_m)} />
                                  <ConnectedMetaRow label={t('connectedTraining.targetSpeed')} value={connectedNumber(segment?.target_speed)} />
                                  <ConnectedMetaRow label={t('connectedTraining.targetIncline')} value={connectedNumber(segment?.target_incline)} />
                                  <ConnectedMetaRow label={t('connectedTraining.targetResistance')} value={connectedNumber(segment?.target_resistance)} />
                                  <ConnectedMetaRow label={t('connectedTraining.targetCadence')} value={connectedNumber(segment?.target_cadence)} />
                                  <ConnectedMetaRow label={t('connectedTraining.targetPower')} value={connectedNumber(segment?.target_power)} />
                                  <ConnectedMetaRow label={t('connectedTraining.targetHeartRateZone')} value={connectedText(segment?.target_heart_rate_zone)} />
                                </div>
                                {segment?.instruction ? (
                                  <p className="mt-2 font-semibold leading-6 text-zinc-600">{segment.instruction}</p>
                                ) : null}
                                {segment?.warning_cue ? (
                                  <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-bold leading-5 text-amber-800 ring-1 ring-amber-100">
                                    {t('connectedTraining.warningCue')}: {segment.warning_cue}
                                  </p>
                                ) : null}
                                {segment?.requires_user_confirmation_before_control ? (
                                  <p className="mt-2 text-xs font-bold text-zinc-500">
                                    {t('connectedTraining.controlRequiresConfirmation')}
                                  </p>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {showPhoneGuidedFallback ? (
                        <div className="mt-3 rounded-lg border border-lime-100 bg-lime-50 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-800">
                                {t('connectedTraining.phoneGuidedTitle')}
                              </p>
                              <p className="mt-1 text-sm font-bold leading-6 text-lime-900">
                                {t(phoneGuidedFallbackMessageKey(delivery))}
                              </p>
                            </div>
                            {phoneGuidedWorkout?.phone_guided_status ? (
                              <ConnectedBadge tone="bg-white text-lime-800 ring-lime-200">
                                {connectedLabel(phoneGuidedWorkout.phone_guided_status)}
                              </ConnectedBadge>
                            ) : null}
                          </div>
                          <p className="mt-2 text-sm leading-6 text-lime-900">
                            {t('connectedTraining.phoneGuidedInstruction')}
                          </p>
                          <p className="mt-2 rounded-lg bg-white px-3 py-2 text-xs font-bold leading-5 text-zinc-600 ring-1 ring-lime-100">
                            {t('connectedTraining.phoneGuidedSyncedDataPreferred')}
                          </p>
                          {hasAutomaticSyncedMatch ? (
                            <p className="mt-3 rounded-lg bg-white px-3 py-2 text-sm font-black text-lime-800 ring-1 ring-lime-100">
                              {t('connectedTraining.syncedWorkoutAutoCompleted')}
                            </p>
                          ) : !phoneGuidedWorkout ? (
                            <button
                              type="button"
                              onClick={() => startPhoneGuidedWorkout(prescription, delivery)}
                              disabled={phoneGuidedStartingId === prescription.id}
                              className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-white px-3 text-sm font-black text-lime-900 transition hover:bg-lime-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {phoneGuidedStartingId === prescription.id ? <Loader2 size={16} className="animate-spin" /> : <Activity size={16} strokeWidth={1.8} />}
                              {t('connectedTraining.phoneGuidedStart')}
                            </button>
                          ) : phoneGuidedWorkout.phone_guided_status === 'completed' ? (
                            <p className="mt-3 rounded-lg bg-white px-3 py-2 text-sm font-black text-lime-800 ring-1 ring-lime-100">
                              {t('connectedTraining.phoneGuidedCompleted')}
                            </p>
                          ) : (
                            <form className="mt-3 rounded-lg bg-white p-3 ring-1 ring-lime-100" onSubmit={(event) => completePhoneGuidedWorkout(event, phoneGuidedWorkout)}>
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                  <p className="text-sm font-black text-zinc-950">
                                    {t('connectedTraining.phoneGuidedManualCompletionTitle')}
                                  </p>
                                  <p className="mt-1 text-xs leading-5 text-zinc-500">
                                    {t('connectedTraining.phoneGuidedManualCompletionBody')}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={onRefresh}
                                  className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 text-xs font-black text-zinc-700 transition hover:bg-zinc-100"
                                >
                                  <RefreshCw size={14} strokeWidth={1.8} />
                                  {t('connectedTraining.phoneGuidedRefreshSyncedData')}
                                </button>
                              </div>
                              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                <label className="block">
                                  <span className="mb-2 block text-xs font-black uppercase tracking-wide text-zinc-500">{t('connectedTraining.durationMinutes')}</span>
                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={completionForm.duration_min}
                                    onChange={(event) => updatePhoneGuidedCompletionForm(phoneGuidedWorkout.id, 'duration_min', event.target.value)}
                                    className="h-10 w-full rounded-lg border border-zinc-200 px-3 text-sm font-bold outline-none transition focus:border-lime-400 focus:ring-4 focus:ring-lime-100"
                                  />
                                </label>
                                <label className="block">
                                  <span className="mb-2 block text-xs font-black uppercase tracking-wide text-zinc-500">{t('connectedTraining.distanceKm')}</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={completionForm.distance_km}
                                    onChange={(event) => updatePhoneGuidedCompletionForm(phoneGuidedWorkout.id, 'distance_km', event.target.value)}
                                    className="h-10 w-full rounded-lg border border-zinc-200 px-3 text-sm font-bold outline-none transition focus:border-lime-400 focus:ring-4 focus:ring-lime-100"
                                  />
                                </label>
                                <label className="block">
                                  <span className="mb-2 block text-xs font-black uppercase tracking-wide text-zinc-500">{t('connectedTraining.manualCalories')}</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={completionForm.calories}
                                    onChange={(event) => updatePhoneGuidedCompletionForm(phoneGuidedWorkout.id, 'calories', event.target.value)}
                                    className="h-10 w-full rounded-lg border border-zinc-200 px-3 text-sm font-bold outline-none transition focus:border-lime-400 focus:ring-4 focus:ring-lime-100"
                                  />
                                </label>
                                <label className="block">
                                  <span className="mb-2 block text-xs font-black uppercase tracking-wide text-zinc-500">{t('connectedTraining.heartRate')}</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={completionForm.heart_rate_avg}
                                    onChange={(event) => updatePhoneGuidedCompletionForm(phoneGuidedWorkout.id, 'heart_rate_avg', event.target.value)}
                                    className="h-10 w-full rounded-lg border border-zinc-200 px-3 text-sm font-bold outline-none transition focus:border-lime-400 focus:ring-4 focus:ring-lime-100"
                                  />
                                </label>
                                <label className="block">
                                  <span className="mb-2 block text-xs font-black uppercase tracking-wide text-zinc-500">{t('connectedTraining.rpe')}</span>
                                  <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    step="1"
                                    value={completionForm.perceived_exertion}
                                    onChange={(event) => updatePhoneGuidedCompletionForm(phoneGuidedWorkout.id, 'perceived_exertion', event.target.value)}
                                    className="h-10 w-full rounded-lg border border-zinc-200 px-3 text-sm font-bold outline-none transition focus:border-lime-400 focus:ring-4 focus:ring-lime-100"
                                  />
                                </label>
                                <label className="block sm:col-span-2 lg:col-span-3">
                                  <span className="mb-2 block text-xs font-black uppercase tracking-wide text-zinc-500">{t('training.notes')}</span>
                                  <textarea
                                    rows={2}
                                    value={completionForm.notes}
                                    onChange={(event) => updatePhoneGuidedCompletionForm(phoneGuidedWorkout.id, 'notes', event.target.value)}
                                    className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm font-bold outline-none transition focus:border-lime-400 focus:ring-4 focus:ring-lime-100"
                                  />
                                </label>
                              </div>
                              <button
                                type="submit"
                                disabled={phoneGuidedCompletingId === phoneGuidedWorkout.id}
                                className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-3 text-sm font-black text-zinc-950 transition hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {phoneGuidedCompletingId === phoneGuidedWorkout.id ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} strokeWidth={1.8} />}
                                {t('connectedTraining.phoneGuidedMarkCompleted')}
                              </button>
                            </form>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="mt-2 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold leading-6 text-zinc-500">
                {t('connectedTraining.noPrescriptions')}
              </p>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
};

const PlannerHero = ({ selectedDays, goals, weeks, history, onGenerate, loading, t }) => (
  <section className="relative overflow-hidden rounded-lg bg-zinc-950 p-5 text-white shadow-sm md:p-7">
    <div className="absolute inset-x-0 top-0 h-px bg-lime-300" />
    <div className="absolute right-0 top-0 h-44 w-44 rounded-full bg-lime-300 opacity-10 blur-3xl" />
    <div className="relative grid gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-end">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-lime-300/30 bg-lime-300/10 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-lime-200">
          <BrainCircuit size={14} strokeWidth={1.8} />
          {t('planner.heroKicker')}
        </div>
        <h1 className="mt-4 text-3xl font-black tracking-normal text-white md:text-4xl">
          {t('planner.title')}
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 md:text-base">
          {t('planner.subtitle')}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <span className="inline-flex min-h-11 items-center rounded-lg bg-lime-300 px-3 text-sm font-black text-zinc-950">
            {t('planner.daysCount', { count: selectedDays.length })}
          </span>
          <span className="inline-flex min-h-11 items-center rounded-lg bg-white/10 px-3 text-sm font-bold text-zinc-100 ring-1 ring-white/10">
            {t('planner.goalsCount', { count: goals.length })}
          </span>
          <span className="inline-flex min-h-11 items-center rounded-lg bg-white/10 px-3 text-sm font-bold text-zinc-100 ring-1 ring-white/10">
            {t(weeks === 1 ? 'planner.weekCountOne' : 'planner.weekCountMany', { count: weeks })}
          </span>
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-white/10 p-4">
        <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-200">
          {t('planner.planningStatus')}
        </p>
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-zinc-950/40 p-3">
            <p className="text-2xl font-black text-white">{history.length}</p>
            <p className="mt-1 text-xs text-zinc-400">{t('planner.historyLabel')}</p>
          </div>
          <div className="rounded-lg bg-zinc-950/40 p-3">
            <p className="text-2xl font-black text-white">{selectedDays.length}</p>
            <p className="mt-1 text-xs text-zinc-400">{t('planner.daysLabel')}</p>
          </div>
          <div className="rounded-lg bg-zinc-950/40 p-3">
            <p className="text-2xl font-black text-white">{goals.length}</p>
            <p className="mt-1 text-xs text-zinc-400">{t('planner.goalsLabel')}</p>
          </div>
        </div>
        <PrimaryButton onClick={onGenerate} disabled={loading} className="mt-5 w-full">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} strokeWidth={1.8} />}
          {loading ? t('common.loading') : t('planner.generatePlan')}
        </PrimaryButton>
      </div>
    </div>
  </section>
);

const DayToggle = ({ day, selected, onToggle, t }) => (
  <button
    type="button"
    onClick={() => onToggle(day.key)}
    className={`min-h-11 min-w-11 rounded-lg border px-3 text-sm font-black transition ${
      selected
        ? 'border-lime-300 bg-lime-300 text-zinc-950'
        : 'border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50 hover:text-zinc-950'
    }`}
  >
    {t(day.labelKey)}
  </button>
);

const GoalTag = ({ label, hasIssue, onRemove }) => (
  <span
    className={`inline-flex min-h-9 items-center gap-2 rounded-full px-3 text-sm font-bold ring-1 ${
      hasIssue
        ? 'bg-amber-50 text-amber-800 ring-amber-100'
        : 'bg-lime-50 text-lime-700 ring-lime-100'
    }`}
  >
    {hasIssue ? <AlertTriangle size={13} strokeWidth={1.8} /> : <Target size={13} strokeWidth={1.8} />}
    {label}
    <button type="button" onClick={onRemove} className="rounded-full text-current opacity-60 hover:opacity-100">
      <X size={13} strokeWidth={2} />
    </button>
  </span>
);

const PlanSessionCard = ({ item, minLabel, t }) => {
  const day = ALL_DAYS.find((option) => option.key === item.day);
  const dayLabel = day ? t(day.labelKey) : item.day;
  return (
    <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.12em] text-lime-700">{dayLabel}</p>
          <h3 className="mt-1 font-black text-zinc-950">{item.focus}</h3>
          <p className="mt-1 text-sm text-zinc-500">{item.duration_min} {minLabel}</p>
        </div>
        {item.rpe_target ? (
          <span className={`rounded-full px-3 py-1 text-xs font-black ring-1 ${rpeTone(item.rpe_target)}`}>
            RPE {item.rpe_target}
          </span>
        ) : null}
      </div>
      <ul className="mt-4 space-y-2">
        {(item.exercises || []).map((exercise, index) => (
          <li key={`${exercise}-${index}`} className="flex items-start gap-2 text-sm leading-5 text-zinc-600">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime-400" />
            {exercise}
          </li>
        ))}
      </ul>
      {item.notes ? <p className="mt-3 text-sm italic leading-5 text-zinc-500">{item.notes}</p> : null}
    </article>
  );
};

const GoalAnalysisPanel = ({ analysis, onApplyImproved, t }) => {
  if (!analysis) return null;
  const { issues, has_conflicts, overall_quality, improved_goals, source, fallback_reason } = analysis;

  if (overall_quality === 'good' && issues.length === 0) {
    return (
      <div className="rounded-lg border border-lime-200 bg-lime-50 p-4 text-sm text-lime-800">
        <div className="flex items-center gap-2 font-black">
          <CheckCircle2 size={16} strokeWidth={1.8} />
          {t('planner.goalAnalysis.goodQuality')}
        </div>
        <SourceMeta source={source} fallbackReason={fallback_reason} />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex flex-wrap items-center gap-2 text-sm font-black text-amber-800">
        <AlertTriangle size={16} strokeWidth={1.8} />
        {t('planner.goalAnalysis.title')}: {overall_quality === 'poor' ? t('planner.goalAnalysis.needsImprovement') : t('planner.goalAnalysis.hasNotes')}
        {has_conflicts ? (
          <span className="rounded-full bg-rose-50 px-2 py-1 text-xs text-rose-700 ring-1 ring-rose-100">
            {t('planner.goalAnalysis.conflict')}
          </span>
        ) : null}
      </div>
      <div className="mt-3 space-y-3">
        {issues.map((issue, index) => (
          <div key={`${issue.goal}-${index}`} className="text-sm">
            <p className="font-black text-zinc-950">{displayGoalLabel(issue.goal, t)}</p>
            <p className="mt-1 text-amber-800">
              {issue.issue_type === 'too_general'
                ? t('planner.goalAnalysis.issueTooGeneral')
                : issue.issue_type === 'conflicting'
                ? t('planner.goalAnalysis.issueConflicting')
                : t('planner.goalAnalysis.issueUnclear')}
            </p>
            <p className="mt-1 text-zinc-600">{issue.suggestion}</p>
          </div>
        ))}
      </div>
      {improved_goals?.length > 0 ? (
        <button
          type="button"
          onClick={() => onApplyImproved(improved_goals)}
          className="mt-4 inline-flex min-h-10 items-center rounded-lg bg-white px-3 text-sm font-black text-amber-800 ring-1 ring-amber-100"
        >
          {t('planner.goalAnalysis.applyImproved')}
        </button>
      ) : null}
      <SourceMeta source={source} fallbackReason={fallback_reason} />
    </div>
  );
};

const RecoveryPanel = ({ recovery, t }) => {
  if (!recovery) return null;
  return (
    <Panel className={`border p-5 ${urgencyTone(recovery.urgency)}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] opacity-80">{t('planner.recovery.title')}</p>
          <h2 className="mt-1 text-xl font-black tracking-normal">{translatedEnumLabel('planner.recovery', recovery.urgency, t)}</h2>
        </div>
        <HeartPulse size={24} strokeWidth={1.8} />
      </div>
      <p className="mt-3 text-sm leading-6 opacity-85">{recovery.rationale}</p>
      <ul className="mt-4 space-y-2">
        {(recovery.recommendations || []).map((item, index) => (
          <li key={`${item}-${index}`} className="flex items-start gap-2 text-sm leading-5">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
            {item}
          </li>
        ))}
      </ul>
      {recovery.recovery_days_suggested > 0 ? (
        <p className="mt-3 text-sm font-black">
          {t('planner.recovery.suggestedDays', { count: recovery.recovery_days_suggested })}
        </p>
      ) : null}
      <SourceMeta source={recovery.source} fallbackReason={recovery.fallback_reason} />
    </Panel>
  );
};

const ProgressPanel = ({ progress }) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (!progress) return null;

  return (
    <Panel className="p-5">
      <button type="button" onClick={() => setExpanded((value) => !value)} className="flex w-full items-start justify-between gap-3 text-left">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
            {t('planner.progress.title', { count: progress.plans_analyzed })}
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-sm">
            <span className="rounded-full bg-zinc-100 px-3 py-1 font-bold text-zinc-700">
              {t('planner.progress.volume')} {TREND_ICON[progress.volume_trend]} {translatedEnumLabel('planner.trends', progress.volume_trend, t)}
            </span>
            <span className="rounded-full bg-zinc-100 px-3 py-1 font-bold text-zinc-700">
              RPE {TREND_ICON[progress.rpe_trend]} {translatedEnumLabel('planner.trends', progress.rpe_trend, t)}
            </span>
          </div>
        </div>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {expanded ? (
        <div className="mt-4 space-y-4">
          <ul className="space-y-2">
            {(progress.insights || []).map((item, index) => (
              <li key={`${item}-${index}`} className="flex items-start gap-2 text-sm leading-5 text-zinc-600">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime-400" />
                {item}
              </li>
            ))}
          </ul>
          {progress.metrics_history?.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-zinc-200">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 text-zinc-500">
                  <tr>
                    <th className="p-2 text-left font-black">{t('common.date')}</th>
                    <th className="p-2 text-right font-black">{t('planner.progress.volumeMin')}</th>
                    <th className="p-2 text-right font-black">{t('planner.progress.sessions')}</th>
                    <th className="p-2 text-right font-black">RPE</th>
                  </tr>
                </thead>
                <tbody>
                  {progress.metrics_history.map((item, index) => (
                    <tr key={item.plan_id || `${item.created_at}-${index}`} className="border-t border-zinc-100">
                      <td className="p-2 text-zinc-600">
                        {new Date(item.created_at).toLocaleDateString(
                          i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'fr' ? 'fr-FR' : 'en-GB',
                          { day: '2-digit', month: '2-digit' },
                        )}
                      </td>
                      <td className="p-2 text-right">{item.total_duration_min}</td>
                      <td className="p-2 text-right">{item.sessions_count}</td>
                      <td className="p-2 text-right">{item.avg_rpe ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <SourceMeta source={progress.source} fallbackReason={progress.fallback_reason} />
        </div>
      ) : null}
    </Panel>
  );
};

const ComparisonPanel = ({ history, onClose }) => {
  const { t, i18n } = useTranslation();
  const [planAId, setPlanAId] = useState('');
  const [planBId, setPlanBId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCompare = async () => {
    if (!planAId || !planBId || planAId === planBId) {
      toast.error(t('planner.compare.selectDifferentPlans'));
      return;
    }
    setLoading(true);
    try {
      const { data } = await trainingPlanner.comparePlans(planAId, planBId);
      setResult(data);
    } catch (err) {
      toast.error(apiErrorMessage(err, t('planner.compare.error'), t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Panel className="p-5">
      <SectionHeader
        icon={<GitCompare size={20} strokeWidth={1.8} />}
        kicker={t('planner.compare.kicker')}
        title={t('planner.compare.title')}
        action={
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-950">
            <X size={18} strokeWidth={1.8} />
          </button>
        }
      />
      <div className="grid gap-3 md:grid-cols-2">
        {[
          ['A', planAId, setPlanAId],
          ['B', planBId, setPlanBId],
        ].map(([label, value, setter]) => (
          <label key={label} className="block">
            <span className="mb-2 block text-sm font-bold text-zinc-700">
              {t('planner.compare.planLabel', { label })}
            </span>
            <select
              value={value}
              onChange={(event) => setter(event.target.value)}
              className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
            >
              <option value="">{t('planner.compare.selectPlan')}</option>
              {history.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {new Date(plan.created_at).toLocaleDateString(
                    i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'fr' ? 'fr-FR' : 'en-GB',
                  )} - {plan.goals?.[0]}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
      <SecondaryButton onClick={handleCompare} disabled={loading} className="mt-4 w-full">
        {loading ? <Loader2 size={14} className="animate-spin" /> : <GitCompare size={14} />}
        {t('planner.compare.compare')}
      </SecondaryButton>
      {result ? (
        <div className="mt-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            {[
              ['A', result.plan_a],
              ['B', result.plan_b],
            ].map(([label, metrics]) => (
              <div key={label} className="rounded-lg bg-zinc-50 p-4">
                <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
                  {t('planner.compare.planLabel', { label })}
                </p>
                <p className="mt-2 text-sm text-zinc-600">
                  <strong className="text-zinc-950">{metrics.total_duration_min}</strong> {t('common.min')}
                </p>
                <p className="mt-1 text-sm text-zinc-600">
                  <strong className="text-zinc-950">{metrics.sessions_count}</strong> {t('planner.progress.sessions').toLowerCase()}
                </p>
                <p className="mt-1 text-sm text-zinc-600">RPE: <strong className="text-zinc-950">{metrics.avg_rpe ?? '-'}</strong></p>
              </div>
            ))}
          </div>
          <div className="rounded-lg bg-lime-50 p-4 text-sm leading-6 text-lime-900 ring-1 ring-lime-100">
            <p>{t('planner.compare.volumeDiff')}: <strong>{result.volume_diff_min > 0 ? '+' : ''}{result.volume_diff_min} {t('common.min')}</strong></p>
            {result.avg_rpe_diff != null ? (
              <p>{t('planner.compare.rpeDiff')}: <strong>{result.avg_rpe_diff > 0 ? '+' : ''}{result.avg_rpe_diff}</strong></p>
            ) : null}
            <p className="mt-2 font-black">{result.recommendation}</p>
          </div>
        </div>
      ) : null}
    </Panel>
  );
};

const PlannerInputs = ({
  selectedDays,
  toggleDay,
  goals,
  setGoals,
  goalInput,
  setGoalInput,
  addGoal,
  goalIssueIds,
  remainingGoalSuggestions,
  handleAnalyzeGoals,
  analyzingGoals,
  goalAnalysis,
  setGoalAnalysis,
  constraints,
  setConstraints,
  weeks,
  setWeeks,
  t,
}) => (
  <Panel className="p-5">
    <SectionHeader
      icon={<Target size={20} strokeWidth={1.8} />}
      kicker={t('planner.planInputs')}
      title={t('planner.trainingBrief')}
      body={t('planner.planInputsBody')}
    />

    <div className="space-y-6">
      <div>
        <p className="mb-3 flex items-center gap-2 text-sm font-black text-zinc-950">
          <CalendarDays size={16} strokeWidth={1.8} />
          {t('common.date')}
        </p>
        <div className="flex flex-wrap gap-2">
          {ALL_DAYS.map((day) => (
            <DayToggle key={day.key} day={day} selected={selectedDays.includes(day.key)} onToggle={toggleDay} t={t} />
          ))}
        </div>
      </div>

      <div>
        <p className="mb-3 flex items-center gap-2 text-sm font-black text-zinc-950">
          <Target size={16} strokeWidth={1.8} />
          {t('planner.title')}
        </p>
        <div className="mb-3 flex flex-wrap gap-2">
          {goals.map((goal) => (
            <GoalTag
              key={goal}
              label={displayGoalLabel(goal, t)}
              hasIssue={goalIssueIds.has(goal)}
              onRemove={() => {
                setGoals(goals.filter((item) => item !== goal));
                setGoalAnalysis(null);
              }}
            />
          ))}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={goalInput}
            onChange={(event) => setGoalInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addGoal(goalInput);
              }
            }}
            placeholder={t('planner.subtitle')}
            className="min-h-11 flex-1 rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
          />
          <SecondaryButton onClick={() => addGoal(goalInput)}>
            <Plus size={15} strokeWidth={1.8} />
            {t('common.add')}
          </SecondaryButton>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {remainingGoalSuggestions.map((suggestion) => (
            <button
              key={suggestion.value}
              type="button"
              onClick={() => addGoal(suggestion.value)}
              className="rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold text-zinc-500 transition hover:border-lime-300 hover:text-zinc-950"
            >
              + {t(suggestion.labelKey)}
            </button>
          ))}
        </div>
        {goals.length > 0 ? (
          <button
            type="button"
            onClick={handleAnalyzeGoals}
            disabled={analyzingGoals}
            className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-lg bg-lime-50 px-3 text-sm font-black text-lime-700 ring-1 ring-lime-100 disabled:opacity-60"
          >
            {analyzingGoals ? <Loader2 size={14} className="animate-spin" /> : <Activity size={14} strokeWidth={1.8} />}
            {t('planner.analyze.button')}
          </button>
        ) : null}
        {goalAnalysis ? (
          <div className="mt-4">
            <GoalAnalysisPanel
              analysis={goalAnalysis}
              t={t}
              onApplyImproved={(improved) => {
                setGoals(improved);
                setGoalAnalysis(null);
                toast.success(t('planner.goalAnalysis.updated'));
              }}
            />
          </div>
        ) : null}
      </div>

      <div>
        <p className="mb-2 flex items-center gap-2 text-sm font-black text-zinc-950">
          <AlertTriangle size={16} strokeWidth={1.8} />
          {t('planner.subtitle')}
        </p>
        <textarea
          value={constraints}
          onChange={(event) => setConstraints(event.target.value)}
          rows={3}
          className="w-full resize-none rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
        />
      </div>

      <div>
        <p className="mb-2 text-sm font-black text-zinc-950">{t('planner.subtitle')}</p>
        <div className="flex gap-2">
          {[1, 2, 3, 4].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setWeeks(item)}
              className={`min-h-11 w-11 rounded-lg border text-sm font-black transition ${
                weeks === item
                  ? 'border-lime-300 bg-lime-300 text-zinc-950'
                  : 'border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>
    </div>
  </Panel>
);

const AIExtrasPanel = ({
  enabled,
  quickTipsContext,
  setQuickTipsContext,
  quickTipsDays,
  setQuickTipsDays,
  quickTipsLoading,
  handleQuickTips,
  quickTipsResult,
  sessionBriefDuration,
  setSessionBriefDuration,
  sessionBriefGoal,
  setSessionBriefGoal,
  sessionBriefConstraints,
  setSessionBriefConstraints,
  sessionBriefLoading,
  handleSessionBrief,
  sessionBriefResult,
}) => {
  if (!enabled.quickTips && !enabled.sessionBrief) {
    return (
      <Panel className="p-5">
        <SectionHeader
          icon={<Video size={20} strokeWidth={1.8} />}
          kicker={t('planner.optionalAnalysis')}
          title={t('planner.videoAnalysis')}
          body={t('planner.noVideoEndpoint')}
        />
      </Panel>
    );
  }

  return (
    <Panel className="p-5">
      <SectionHeader
        icon={<Sparkles size={20} strokeWidth={1.8} />}
        kicker={t('planner.aiExtras')}
        title={t('planner.sessionTools')}
        body={t('planner.aiExtrasBody')}
      />
      <div className="grid gap-5">
        {enabled.quickTips ? (
          <div className="rounded-lg bg-zinc-50 p-4">
            <p className="font-black text-zinc-950">{t('planner.quickTips')}</p>
            <textarea
              value={quickTipsContext}
              onChange={(event) => setQuickTipsContext(event.target.value)}
              rows={2}
              maxLength={320}
              placeholder={t('planner.sessionContextPlaceholder')}
              className="mt-3 w-full resize-none rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
            />
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                type="number"
                min={1}
                max={7}
                value={quickTipsDays}
                onChange={(event) => setQuickTipsDays(Number(event.target.value))}
                className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100 sm:w-28"
              />
              <SecondaryButton onClick={handleQuickTips} disabled={quickTipsLoading}>
                {quickTipsLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {t('planner.getQuickTips')}
              </SecondaryButton>
            </div>
            {quickTipsResult?.tips?.length > 0 ? (
              <ul className="mt-4 space-y-2">
                {quickTipsResult.tips.map((tip, index) => (
                  <li key={`tip-${tip}-${index}`} className="flex items-start gap-2 text-sm leading-5 text-zinc-600">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime-400" />
                    {tip}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {enabled.sessionBrief ? (
          <div className="rounded-lg bg-zinc-50 p-4">
            <p className="font-black text-zinc-950">{t('planner.sessionBrief')}</p>
            <div className="mt-3 grid gap-3">
              <input
                type="number"
                min={30}
                max={240}
                value={sessionBriefDuration}
                onChange={(event) => setSessionBriefDuration(Number(event.target.value))}
                className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              />
              <input
                type="text"
                value={sessionBriefGoal}
                onChange={(event) => setSessionBriefGoal(event.target.value)}
                maxLength={140}
                placeholder={t('planner.keyGoalPlaceholder')}
                className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              />
              <textarea
                value={sessionBriefConstraints}
                onChange={(event) => setSessionBriefConstraints(event.target.value)}
                rows={2}
                placeholder={t('planner.constraintsPlaceholder')}
                className="w-full resize-none rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
              />
              <SecondaryButton onClick={handleSessionBrief} disabled={sessionBriefLoading}>
                {sessionBriefLoading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                {t('planner.generateSessionBrief')}
              </SecondaryButton>
            </div>
            {sessionBriefResult?.brief ? (
              <div className="mt-4 rounded-lg border border-zinc-200 bg-white p-4 text-sm leading-6 text-zinc-600">
                <p>{sessionBriefResult.brief}</p>
                {sessionBriefResult.checklist?.length > 0 ? (
                  <ul className="mt-3 space-y-2">
                    {sessionBriefResult.checklist.map((item, index) => (
                      <li key={`brief-${item}-${index}`} className="flex items-start gap-2">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </Panel>
  );
};

const PlanResult = ({ plan, history, showComparison, setShowComparison, exportingPdf, handleExportPdf, t }) => {
  const grouped = groupedPlanItems(plan.schedule);

  return (
    <div className="space-y-5">
      <Panel className="p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">{t('planner.generatedPlan')}</p>
            <h2 className="mt-1 text-2xl font-black tracking-normal text-zinc-950">{t('planner.title')}</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-500">{plan.summary}</p>
            <SourceMeta source={plan.source} fallbackReason={plan.fallback_reason} />
          </div>
          <div className="flex flex-wrap gap-2 md:justify-end">
            {(plan.goals || []).map((goal) => (
              <span key={goal} className="rounded-full bg-lime-50 px-3 py-1 text-xs font-black text-lime-700 ring-1 ring-lime-100">
                {displayGoalLabel(goal, t)}
              </span>
            ))}
            <SecondaryButton onClick={handleExportPdf} disabled={exportingPdf}>
              {exportingPdf ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {t('planner.exportPdf')}
            </SecondaryButton>
          </div>
        </div>
        {plan.load_analysis ? (
          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <div className="rounded-lg bg-zinc-50 p-4">
              <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">{t('planner.progress.sessions')}</p>
              <p className="mt-2 text-2xl font-black text-zinc-950">{plan.load_analysis.total_sessions_28d}</p>
            </div>
            <div className="rounded-lg bg-zinc-50 p-4">
              <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">{t('common.min')}</p>
              <p className="mt-2 text-2xl font-black text-zinc-950">{plan.load_analysis.total_duration_min_28d}</p>
            </div>
            <div className="rounded-lg bg-zinc-50 p-4">
              <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">RPE</p>
              <p className="mt-2 text-2xl font-black text-zinc-950">{plan.load_analysis.avg_rpe ?? '-'}</p>
            </div>
          </div>
        ) : null}
      </Panel>

      {RECOMMENDATION_GROUPS.map((group) => {
        const items = grouped[group.key] || [];
        if (items.length === 0) return null;
        return (
          <Panel key={group.key} className="p-5">
            <SectionHeader
              icon={group.icon}
              kicker={t('planner.recommendations')}
              title={t(group.titleKey)}
              action={
                <span className={`rounded-full px-3 py-1 text-xs font-black ring-1 ${group.tone}`}>
                  {items.length}
                </span>
              }
            />
            <div className="grid gap-3 md:grid-cols-2">
              {items.map((item, index) => (
                <PlanSessionCard key={`${item.day}-${item.focus}-${index}`} item={item} minLabel={t('common.min')} t={t} />
              ))}
            </div>
          </Panel>
        );
      })}

      {history.length >= 2 ? (
        <div>
          {showComparison ? (
            <ComparisonPanel history={history} onClose={() => setShowComparison(false)} />
          ) : (
            <SecondaryButton onClick={() => setShowComparison(true)}>
              <GitCompare size={15} />
              {t('planner.compare.withAnother')}
            </SecondaryButton>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default function TrainingPlannerPage() {
  const { t, i18n } = useTranslation();
  const [selectedDays, setSelectedDays] = useState([]);
  const [goals, setGoals] = useState([]);
  const [goalInput, setGoalInput] = useState('');
  const [constraints, setConstraints] = useState('');
  const [weeks, setWeeks] = useState(1);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [goalAnalysis, setGoalAnalysis] = useState(null);
  const [analyzingGoals, setAnalyzingGoals] = useState(false);
  const [recovery, setRecovery] = useState(null);
  const [progress, setProgress] = useState(null);
  const [history, setHistory] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [aiExtrasEnabled, setAiExtrasEnabled] = useState({ quickTips: false, sessionBrief: false });
  const [quickTipsContext, setQuickTipsContext] = useState('');
  const [quickTipsDays, setQuickTipsDays] = useState(3);
  const [quickTipsLoading, setQuickTipsLoading] = useState(false);
  const [quickTipsResult, setQuickTipsResult] = useState(null);
  const [sessionBriefDuration, setSessionBriefDuration] = useState(60);
  const [sessionBriefGoal, setSessionBriefGoal] = useState('');
  const [sessionBriefConstraints, setSessionBriefConstraints] = useState('');
  const [sessionBriefLoading, setSessionBriefLoading] = useState(false);
  const [sessionBriefResult, setSessionBriefResult] = useState(null);
  const [developmentProfile, setDevelopmentProfile] = useState(null);
  const [developmentProfileLoading, setDevelopmentProfileLoading] = useState(true);
  const [developmentProfileFailed, setDevelopmentProfileFailed] = useState(false);
  const [headCoachStrategy, setHeadCoachStrategy] = useState(null);
  const [headCoachStrategyLoading, setHeadCoachStrategyLoading] = useState(false);
  const [headCoachStrategyFailed, setHeadCoachStrategyFailed] = useState(false);
  const [connectedTrainingData, setConnectedTrainingData] = useState({
    devices: [],
    metrics: [],
    workouts: [],
    prescriptions: [],
    imports: [],
    mobileStatuses: [],
    equipmentCapabilities: [],
    equipmentDeliveries: [],
    phoneGuidedWorkouts: [],
    syncedWorkoutMatches: [],
  });
  const [connectedTrainingLoading, setConnectedTrainingLoading] = useState(false);
  const [connectedTrainingFailed, setConnectedTrainingFailed] = useState(false);

  const remainingGoalSuggestions = useMemo(
    () => GOAL_SUGGESTIONS.filter((suggestion) => !goals.includes(suggestion.value)),
    [goals],
  );

  const loadHeadCoachStrategy = useCallback(async () => {
    setHeadCoachStrategyLoading(true);
    setHeadCoachStrategyFailed(false);
    try {
      const response = await headCoach.getStrategy();
      setHeadCoachStrategy(response?.data || null);
    } catch {
      setHeadCoachStrategy(null);
      setHeadCoachStrategyFailed(true);
    } finally {
      setHeadCoachStrategyLoading(false);
    }
  }, []);

  const loadConnectedTrainingData = useCallback(async () => {
    setConnectedTrainingLoading(true);
    setConnectedTrainingFailed(false);
    try {
      const [
        devicesResponse,
        metricsResponse,
        workoutsResponse,
        prescriptionsResponse,
        importsResponse,
        mobileStatusResponse,
        equipmentCapabilitiesResponse,
        equipmentDeliveriesResponse,
        phoneGuidedWorkoutsResponse,
        syncedWorkoutMatchesResponse,
      ] = await Promise.all([
        connectedTraining.getDevices(),
        connectedTraining.getRecentFitnessMetrics({ limit: 12 }),
        connectedTraining.getRecentEquipmentWorkouts({ limit: 6 }),
        connectedTraining.getWorkoutPrescriptions(),
        connectedTraining.getRecentImports({ limit: 5 }),
        connectedTraining.getMobileSourceStatuses(),
        connectedTraining.getRecentEquipmentCapabilities({ limit: 20 }),
        connectedTraining.getRecentEquipmentWorkoutDeliveries({ limit: 20 }),
        connectedTraining.getRecentPhoneGuidedEquipmentWorkouts({ limit: 20 }),
        connectedTraining.getRecentSyncedWorkoutMatches({ limit: 20 }),
      ]);
      setConnectedTrainingData({
        devices: devicesResponse?.data || [],
        metrics: metricsResponse?.data || [],
        workouts: workoutsResponse?.data || [],
        prescriptions: prescriptionsResponse?.data || [],
        imports: importsResponse?.data || [],
        mobileStatuses: mobileStatusResponse?.data || [],
        equipmentCapabilities: equipmentCapabilitiesResponse?.data || [],
        equipmentDeliveries: equipmentDeliveriesResponse?.data || [],
        phoneGuidedWorkouts: phoneGuidedWorkoutsResponse?.data || [],
        syncedWorkoutMatches: syncedWorkoutMatchesResponse?.data || [],
      });
    } catch {
      setConnectedTrainingData({
        devices: [],
        metrics: [],
        workouts: [],
        prescriptions: [],
        imports: [],
        mobileStatuses: [],
        equipmentCapabilities: [],
        equipmentDeliveries: [],
        phoneGuidedWorkouts: [],
        syncedWorkoutMatches: [],
      });
      setConnectedTrainingFailed(true);
    } finally {
      setConnectedTrainingLoading(false);
    }
  }, []);

  useEffect(() => {
    trainingPlanner.getRecovery()
      .then((response) => setRecovery(response.data))
      .catch(() => {});
    trainingPlanner.getProgress(5)
      .then((response) => setProgress(response.data))
      .catch(() => {});
    trainingPlanner.getHistory(10)
      .then((response) => setHistory(response.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    aiExtras.getContract()
      .then((response) => {
        const contract = response?.data || {};
        setAiExtrasEnabled(resolveEnabledAiExtras(contract));
      })
      .catch(() => {
        setAiExtrasEnabled({ quickTips: false, sessionBrief: false });
      });
  }, []);

  useEffect(() => {
    let mounted = true;
    setDevelopmentProfileLoading(true);
    setDevelopmentProfileFailed(false);
    athleteDevelopment.getProfile()
      .then((response) => {
        if (!mounted) return;
        setDevelopmentProfile(response.data || null);
      })
      .catch(() => {
        if (!mounted) return;
        setDevelopmentProfile(null);
        setDevelopmentProfileFailed(true);
      })
      .finally(() => {
        if (mounted) setDevelopmentProfileLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    loadHeadCoachStrategy();
  }, [loadHeadCoachStrategy]);

  useEffect(() => {
    loadConnectedTrainingData();
  }, [loadConnectedTrainingData]);

  const toggleDay = (key) => {
    setSelectedDays((prev) =>
      prev.includes(key) ? prev.filter((day) => day !== key) : [...prev, key],
    );
  };

  const addGoal = (text) => {
    const trimmed = text.trim();
    if (trimmed && !goals.includes(trimmed)) {
      setGoals((prev) => [...prev, trimmed]);
      setGoalAnalysis(null);
    }
    setGoalInput('');
  };

  const handleAnalyzeGoals = async () => {
    if (goals.length === 0) {
      toast.error(t('planner.analyze.addGoalsFirst'));
      return;
    }
    setAnalyzingGoals(true);
    try {
      const { data } = await trainingPlanner.analyzeGoals({
        goals,
        available_days: selectedDays.length ? selectedDays : ['monday'],
        weeks,
      });
      setGoalAnalysis(data);
    } catch (err) {
      toast.error(apiErrorMessage(err, t('planner.analyze.error'), t));
    } finally {
      setAnalyzingGoals(false);
    }
  };

  const handleGenerate = async () => {
    if (selectedDays.length === 0) {
      toast.error(t('planner.selectDayError'));
      return;
    }
    if (goals.length === 0) {
      toast.error(t('planner.addGoalError'));
      return;
    }

    setLoading(true);
    setPlan(null);
    try {
      const constraintList = constraints
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);
      const { data } = await trainingPlanner.generate({
        goals,
        available_days: selectedDays,
        constraints: constraintList,
        weeks,
      });
      setPlan(data);
      trainingPlanner.getHistory(10).then((response) => setHistory(response.data)).catch(() => {});
      trainingPlanner.getProgress(5).then((response) => setProgress(response.data)).catch(() => {});
      toast.success(t('planner.generateSuccess'));
    } catch (err) {
      toast.error(apiErrorMessage(err, t('common.error'), t));
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    if (!plan) return;
    setExportingPdf(true);
    try {
      const { data } = await trainingPlanner.exportPdf(plan.id);
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `training_plan_${plan.id.slice(0, 8)}.pdf`;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiErrorMessage(err, t('planner.exportPdfError'), t));
    } finally {
      setExportingPdf(false);
    }
  };

  const handleQuickTips = async () => {
    setQuickTipsLoading(true);
    setQuickTipsResult(null);
    try {
      const { data } = await aiExtras.getQuickTips({
        focus_area: 'consistency',
        intensity: 'medium',
        context: quickTipsContext,
        days_per_week: quickTipsDays,
      });
      setQuickTipsResult(data);
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || t('common.error'), t));
    } finally {
      setQuickTipsLoading(false);
    }
  };

  const handleSessionBrief = async () => {
    setSessionBriefLoading(true);
    setSessionBriefResult(null);
    try {
      const constraintsList = sessionBriefConstraints
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);
      const { data } = await aiExtras.getSessionBrief({
        session_type: 'practice',
        duration_min: sessionBriefDuration,
        key_goal: sessionBriefGoal,
        constraints: constraintsList,
      });
      setSessionBriefResult(data);
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || t('common.error'), t));
    } finally {
      setSessionBriefLoading(false);
    }
  };

  const goalIssueIds = goalAnalysis
    ? new Set(goalAnalysis.issues.map((issue) => issue.goal))
    : new Set();

  return (
    <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="training-planner-page">
      <div className="mx-auto max-w-7xl space-y-5">
        <PlannerHero
          selectedDays={selectedDays}
          goals={goals}
          weeks={weeks}
          history={history}
          onGenerate={handleGenerate}
          loading={loading}
          t={t}
        />

        <div className="grid gap-5 xl:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-5">
            <PlannerInputs
              selectedDays={selectedDays}
              toggleDay={toggleDay}
              goals={goals}
              setGoals={setGoals}
              goalInput={goalInput}
              setGoalInput={setGoalInput}
              addGoal={addGoal}
              goalIssueIds={goalIssueIds}
              remainingGoalSuggestions={remainingGoalSuggestions}
              handleAnalyzeGoals={handleAnalyzeGoals}
              analyzingGoals={analyzingGoals}
              goalAnalysis={goalAnalysis}
              setGoalAnalysis={setGoalAnalysis}
              constraints={constraints}
              setConstraints={setConstraints}
              weeks={weeks}
              setWeeks={setWeeks}
              t={t}
            />

            {plan ? (
              <PlanResult
                plan={plan}
                history={history}
                showComparison={showComparison}
                setShowComparison={setShowComparison}
                exportingPdf={exportingPdf}
                handleExportPdf={handleExportPdf}
                t={t}
              />
            ) : (
              <Panel className="p-8 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-zinc-950 text-lime-300">
                  <Wand2 size={26} strokeWidth={1.8} />
                </div>
                <h2 className="mt-4 text-2xl font-black tracking-normal text-zinc-950">
                  {t('planner.emptyTitle')}
                </h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
                  {t('planner.emptyBody')}
                </p>
              </Panel>
            )}
          </div>

          <aside className="space-y-5">
            <DevelopmentProfileContextPanel
              profile={developmentProfile}
              loading={developmentProfileLoading}
              loadFailed={developmentProfileFailed}
              language={i18n.language}
              t={t}
            />
            <HeadCoachStrategyPanel
              strategy={headCoachStrategy}
              loading={headCoachStrategyLoading}
              loadFailed={headCoachStrategyFailed}
              onRefresh={loadHeadCoachStrategy}
              t={t}
            />
            <ConnectedTrainingPanel
              data={connectedTrainingData}
              loading={connectedTrainingLoading}
              loadFailed={connectedTrainingFailed}
              onRefresh={loadConnectedTrainingData}
              language={i18n.language}
              t={t}
            />
            <VideoAnalysisFoundationPanel language={i18n.language} t={t} />
            <RecoveryPanel recovery={recovery} t={t} />
            {progress && progress.plans_analyzed > 0 ? <ProgressPanel progress={progress} /> : null}
            <AIExtrasPanel
              enabled={aiExtrasEnabled}
              quickTipsContext={quickTipsContext}
              setQuickTipsContext={setQuickTipsContext}
              quickTipsDays={quickTipsDays}
              setQuickTipsDays={setQuickTipsDays}
              quickTipsLoading={quickTipsLoading}
              handleQuickTips={handleQuickTips}
              quickTipsResult={quickTipsResult}
              sessionBriefDuration={sessionBriefDuration}
              setSessionBriefDuration={setSessionBriefDuration}
              sessionBriefGoal={sessionBriefGoal}
              setSessionBriefGoal={setSessionBriefGoal}
              sessionBriefConstraints={sessionBriefConstraints}
              setSessionBriefConstraints={setSessionBriefConstraints}
              sessionBriefLoading={sessionBriefLoading}
              handleSessionBrief={handleSessionBrief}
              sessionBriefResult={sessionBriefResult}
            />
            <Panel className="p-5">
              <SectionHeader
                icon={<BrainCircuit size={20} strokeWidth={1.8} />}
                kicker={t('planner.scopeKicker')}
                title={t('planner.supportedOutputs')}
                body={t('planner.scopeBody')}
              />
              <div className="space-y-2">
                {RECOMMENDATION_GROUPS.map((group) => (
                  <div key={group.key} className="flex items-center gap-3 rounded-lg bg-zinc-50 px-3 py-2">
                    <span className={`flex h-8 w-8 items-center justify-center rounded-lg ring-1 ${group.tone}`}>
                      {group.icon}
                    </span>
                    <span className="text-sm font-black text-zinc-700">{t(group.titleKey)}</span>
                  </div>
                ))}
              </div>
            </Panel>
          </aside>
        </div>
      </div>
    </div>
  );
}
